import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState, useEffect, useMemo } from 'react'
import { Calendar, User, Users, Code, ExternalLink, Check, CheckCircle2, Circle, Plus, Edit, Server, Github, Figma, BookOpen, Building2, TrendingUp, Trash2, GripVertical, Shield, Euro, Clock, Sparkles, Wrench, Eye, ScrollText } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  useDroppable,
  DragOverEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button } from '@/components/ui/button'
import { PageSkeleton } from '@/components/ui/page-skeleton'
import { TextWithLinks } from '@/components/ui/text-with-links'
import { Badge } from '@/components/ui/badge'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { CreateTaskModal, type TaskFormData } from '@/components/projects/CreateTaskModal'
import { CreateMetricModal, type MetricFormData } from '@/components/projects/CreateMetricModal'
import { EditMetricModal, type MetricEditData } from '@/components/projects/EditMetricModal'
import { UpdateProjectModal, type ProjectFormData } from '@/components/projects/UpdateProjectModal'
import { CreateRoadmapStepModal, type RoadmapStepFormData } from '@/components/projects/CreateRoadmapStepModal'
import { EditRoadmapStepModal, type RoadmapStepEditData } from '@/components/projects/EditRoadmapStepModal'
import { EditClientEmailModal } from '@/components/projects/EditClientEmailModal'
import { EditProjectManagerModal } from '@/components/projects/EditProjectManagerModal'
import { EditFinancialInfoModal } from '@/components/projects/EditFinancialInfoModal'
import { ClientViewConfigModal } from '@/components/projects/ClientViewConfigModal'
import {
  ProjectTimeContributorsModal,
  type ProjectTimeContributorsData,
} from '@/components/projects/ProjectTimeContributorsModal'
import { EditTaskModal, type TaskEditData } from '@/components/projects/EditTaskModal'
import { RoadmapSection, type RoadmapStep } from '@/components/projects/RoadmapSection'
import { MaintenanceSection, type Maintenance } from '@/components/projects/MaintenanceSection'
import { CreateMaintenanceModal, type MaintenanceFormData } from '@/components/projects/CreateMaintenanceModal'
import { EditMaintenanceModal, type MaintenanceEditData } from '@/components/projects/EditMaintenanceModal'
import { DeleteModal } from '@/components/ui/delete-modal'
import { ConfirmModal } from '@/components/ui/confirm-modal'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { api } from '@/services/api'
import { useClientPreviewLayout } from '@/contexts/ClientPreviewContext'
import { useAuth } from '@/contexts/AuthContext'
import { HeroBanner } from '@/components/layout/HeroBanner'
import { getProjectStatusStyle } from '@/lib/projectStatus'

export const Route = createFileRoute('/projects/$projectId')({
  component: ProjectDetail,
  // ?vue=client : ouvre la fiche en Vue client (aperçu lancé depuis la liste)
  validateSearch: (search: Record<string, unknown>): { vue?: 'client' } =>
    search.vue === 'client' ? { vue: 'client' } : {},
})

interface ProjectURL {
  id: number
  url_type: string
  url: string
  created_at: string
}

interface ProjectDetail {
  id: number
  nom: string
  description: string | null
  description_interne: string | null
  date_kickoff: string
  stack_technique: string | null
  noms_devs: string | null
  developer_ids?: string[]
  status: string
  visibility?: string
  tasks_visible_client?: boolean
  roadmap_visible_client?: boolean
  metrics_visible_client?: boolean
  client_id: string
  project_manager_id: string
  project_manager_ids?: string[]
  project_managers?: Array<{ id: string; nom: string | null; email: string | null }>
  activity_sector_id: number | null
  created_by_id: string
  created_at: string
  updated_at: string | null
  urls: ProjectURL[]
  client_nom: string | null
  client_email: string | null
  project_manager_nom: string | null
  project_manager_email: string | null
  activity_sector_nom: string | null
  // Informations financières
  montant_facture_build?: number | null
  montant_run?: number | null
  couts?: number | null
  jours_vendu?: number | null
  jours_reel?: number | null
  payment_status_id?: number | null
  payment_status_nom?: string | null
}

interface Task {
  id: number
  titre: string
  description: string | null
  description_client?: string | null
  type?: string
  image_url?: string | null
  status: string
  date_creation: string
  date_completion: string | null
  deadline?: string | null
  deadline_visible_client?: boolean
  ordre: number
  visibilite_client: boolean
  requires_prod_test?: boolean
  project_id: number
  created_by_id?: string
  created_by_email?: string
  completed_by_id?: string
  completed_by_email?: string
  created_at: string
  updated_at: string | null
}

type ProdTestConfirmAction =
  | { type: 'toggle'; taskId: number }
  | { type: 'move'; taskId: number; newStatus: 'a_effectuer' | 'realisee'; newIndex: number }

interface MetricData {
  id: number
  metric_id: number
  value: string
  created_at: string
  date?: string
}

interface Metric {
  id: number
  nom: string
  description: string | null
  unite: string | null
  mode_calcul?: string
  visibilite_client: boolean
  api_token: string
  api_url: string
  data: MetricData[]
}

function ProjectDetail() {
  const navigate = useNavigate()
  const { projectId } = Route.useParams()
  const { vue } = Route.useSearch()
  const { user } = useAuth()
  const [project, setProject] = useState<ProjectDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [tasks, setTasks] = useState<Task[]>([])
  const [loadingTasks, setLoadingTasks] = useState(false)
  const [metrics, setMetrics] = useState<Metric[]>([])
  const [loadingMetrics, setLoadingMetrics] = useState(false)
  const [roadmapSteps, setRoadmapSteps] = useState<RoadmapStep[]>([])
  const [loadingRoadmap, setLoadingRoadmap] = useState(false)
  const [maintenances, setMaintenances] = useState<Maintenance[]>([])
  const [loadingMaintenances, setLoadingMaintenances] = useState(false)
  const [showTaskModal, setShowTaskModal] = useState(false)
  const [showMetricModal, setShowMetricModal] = useState(false)
  const [showRoadmapModal, setShowRoadmapModal] = useState(false)
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false)
  const [showUpdateModal, setShowUpdateModal] = useState(false)
  const [isCreatingTask, setIsCreatingTask] = useState(false)
  const [isCreatingMetric, setIsCreatingMetric] = useState(false)
  const [isCreatingRoadmapStep, setIsCreatingRoadmapStep] = useState(false)
  const [isCreatingMaintenance, setIsCreatingMaintenance] = useState(false)
  const [isUpdatingProject, setIsUpdatingProject] = useState(false)
  const [updatingTaskId, setUpdatingTaskId] = useState<number | null>(null)
  const [taskToDelete, setTaskToDelete] = useState<{ id: number; title: string } | null>(null)
  const [isDeletingTask, setIsDeletingTask] = useState(false)
  const [showEditTaskModal, setShowEditTaskModal] = useState(false)
  const [taskToEdit, setTaskToEdit] = useState<TaskEditData | null>(null)
  const [isUpdatingTask, setIsUpdatingTask] = useState(false)
  const [taskToView, setTaskToView] = useState<Task | null>(null)
  const [prodTestConfirm, setProdTestConfirm] = useState<ProdTestConfirmAction | null>(null)
  const [metricSortOrder, setMetricSortOrder] = useState<'newest' | 'oldest'>('newest')
  const [roadmapSortOrder, setRoadmapSortOrder] = useState<'oldest' | 'newest'>('oldest')
  const [maintenanceSortOrder, setMaintenanceSortOrder] = useState<'oldest' | 'newest'>('newest')
  const [isDeletingProject, setIsDeletingProject] = useState(false)
  const [showDeleteProjectModal, setShowDeleteProjectModal] = useState(false)

  // Metric edit/delete states
  const [showEditMetricModal, setShowEditMetricModal] = useState(false)
  const [metricToEdit, setMetricToEdit] = useState<MetricEditData | null>(null)
  const [isUpdatingMetric, setIsUpdatingMetric] = useState(false)
  const [metricToDelete, setMetricToDelete] = useState<{ id: number; name: string } | null>(null)
  const [isDeletingMetric, setIsDeletingMetric] = useState(false)

  // Roadmap step edit/delete states
  const [showEditRoadmapModal, setShowEditRoadmapModal] = useState(false)
  const [roadmapStepToEdit, setRoadmapStepToEdit] = useState<RoadmapStepEditData | null>(null)
  const [isUpdatingRoadmapStep, setIsUpdatingRoadmapStep] = useState(false)
  const [roadmapStepToDelete, setRoadmapStepToDelete] = useState<{ id: number; title: string } | null>(null)
  const [isDeletingRoadmapStep, setIsDeletingRoadmapStep] = useState(false)

  // Maintenance edit/delete states
  const [showEditMaintenanceModal, setShowEditMaintenanceModal] = useState(false)
  const [maintenanceToEdit, setMaintenanceToEdit] = useState<MaintenanceEditData | null>(null)
  const [isUpdatingMaintenance, setIsUpdatingMaintenance] = useState(false)
  const [maintenanceToDelete, setMaintenanceToDelete] = useState<{ id: number; title: string } | null>(null)
  const [isDeletingMaintenance, setIsDeletingMaintenance] = useState(false)

  // Client email edit state
  const [showEditClientEmailModal, setShowEditClientEmailModal] = useState(false)

  // Project manager edit state
  const [showEditProjectManagerModal, setShowEditProjectManagerModal] = useState(false)

  // Financial info edit state
  const [showEditFinancialModal, setShowEditFinancialModal] = useState(false)
  const [showClientViewConfigModal, setShowClientViewConfigModal] = useState(false)
  const [showTimeContributorsModal, setShowTimeContributorsModal] = useState(false)
  const [timeContributors, setTimeContributors] = useState<ProjectTimeContributorsData | null>(null)
  const [loadingTimeContributors, setLoadingTimeContributors] = useState(false)

  // View mode toggle (admin only)
  const [viewMode, setViewMode] = useState<'admin' | 'client'>(vue === 'client' ? 'client' : 'admin')

  // Check if user is admin (must be defined before useEffect)
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin'

  // Check if user is super admin (has super_admin role)
  const isSuperAdmin = user?.role === 'super_admin'

  // Determine effective view mode (for display logic)
  const effectiveViewMode = isAdmin ? viewMode : 'client'

  // En Vue client, bandeau client à la place de la sidebar
  useClientPreviewLayout(isAdmin && viewMode === 'client')

  const showTasksSection =
    effectiveViewMode === 'admin' || (project?.tasks_visible_client ?? true)
  const showMetricsSection =
    effectiveViewMode === 'admin' || (project?.metrics_visible_client ?? true)
  const showRoadmapSection =
    effectiveViewMode === 'admin' || (project?.roadmap_visible_client ?? true)

  // Fetch project details
  useEffect(() => {
    const fetchProject = async () => {
      setLoading(true)
      try {
        // Only pass view_mode if user is admin and viewMode is 'client'
        const params = isAdmin && viewMode === 'client' ? { view_mode: 'client' } : undefined
        const response = await api.get<ProjectDetail>(`/projects/${projectId}`, params)
        setProject(response.data)
      } catch (error) {
        console.error('Erreur lors de la récupération du projet:', error)
        navigate({ to: '/projects' })
      } finally {
        setLoading(false)
      }
    }

    if (projectId) {
      fetchProject()
    }
  }, [projectId, navigate, viewMode, isAdmin])

  // Fetch tasks
  useEffect(() => {
    const fetchTasks = async () => {
      if (!projectId) return
      setLoadingTasks(true)
      try {
        const response = await api.get<{ data: Task[]; count: number; message: string }>(`/projects/${projectId}/tasks`)
        setTasks(response.data.data || [])
      } catch (error) {
        console.error('Erreur lors de la récupération des tâches:', error)
      } finally {
        setLoadingTasks(false)
      }
    }

    if (projectId) {
      fetchTasks()
    }
  }, [projectId])

  // Temps passé (standup) — super admin (section financière)
  useEffect(() => {
    const fetchTimeContributors = async () => {
      if (!projectId || !isSuperAdmin) return
      setLoadingTimeContributors(true)
      try {
        const response = await api.get<ProjectTimeContributorsData>(`/projects/${projectId}/time-contributors`)
        setTimeContributors({
          total_hours: response.data.total_hours,
          total_days: response.data.total_days,
          contributors_count: response.data.contributors_count,
          contributors: response.data.contributors || [],
          ticket_times: response.data.ticket_times || [],
          ticket_days_total: response.data.ticket_days_total || 0,
        })
      } catch (error) {
        console.error('Erreur chargement temps contributeurs:', error)
        setTimeContributors(null)
      } finally {
        setLoadingTimeContributors(false)
      }
    }
    if (projectId && isSuperAdmin) {
      fetchTimeContributors()
    }
  }, [projectId, isSuperAdmin])

  // Fetch metrics
  useEffect(() => {
    const fetchMetrics = async () => {
      if (!projectId) return
      setLoadingMetrics(true)
      try {
        const response = await api.get<{ data: Metric[]; count: number; message: string }>(`/projects/${projectId}/metrics`)
        setMetrics(response.data.data || [])
      } catch (error) {
        console.error('Erreur lors de la récupération des métriques:', error)
      } finally {
        setLoadingMetrics(false)
      }
    }

    if (projectId) {
      fetchMetrics()
    }
  }, [projectId])

  // Fetch roadmap steps
  useEffect(() => {
    const fetchRoadmapSteps = async () => {
      if (!projectId) return
      setLoadingRoadmap(true)
      try {
        const response = await api.get<{ data: RoadmapStep[]; count: number; message: string }>(`/projects/${projectId}/roadmap`)
        setRoadmapSteps(response.data.data || [])
      } catch (error) {
        console.error('Erreur lors de la récupération des étapes de roadmap:', error)
      } finally {
        setLoadingRoadmap(false)
      }
    }

    if (projectId) {
      fetchRoadmapSteps()
    }
  }, [projectId])

  // Fetch maintenances (admin only)
  useEffect(() => {
    const fetchMaintenances = async () => {
      if (!projectId || !isAdmin) return
      setLoadingMaintenances(true)
      try {
        const response = await api.get<{ data: Maintenance[]; count: number; message: string }>(`/projects/${projectId}/maintenance`)
        setMaintenances(response.data.data || [])
      } catch (error) {
        console.error('Erreur lors de la récupération des maintenances:', error)
      } finally {
        setLoadingMaintenances(false)
      }
    }

    if (projectId && isAdmin) {
      fetchMaintenances()
    }
  }, [projectId, isAdmin])

  // Format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    const day = date.getDate()
    const monthNames = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.']
    const month = monthNames[date.getMonth()]
    const year = date.getFullYear()
    return `${day} ${month} ${year}`
  }

  // Parse stack technique
  const getStackTags = (stackTechnique: string | null): string[] => {
    if (!stackTechnique) return []
    return stackTechnique.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
  }

  // Parse developers
  const getDevelopers = (nomsDevs: string | null): string[] => {
    if (!nomsDevs) return []
    return nomsDevs.split(',').map(name => name.trim()).filter(name => name.length > 0)
  }

  // Get URLs by type (returns all URLs of that type)
  const getUrlsByType = (urls: ProjectURL[], type: string): string[] => {
    const normalizedType = type.toLowerCase()
    return urls
      .filter((u) => String(u.url_type).toLowerCase() === normalizedType)
      .map((u) => u.url)
  }


  // Separate tasks by status - MUST be before conditional returns (Rules of Hooks)
  const todoTasks = useMemo(() => {
    return tasks.filter(task => task.status === 'a_effectuer')
  }, [tasks])

  const completedTasks = useMemo(() => {
    return tasks.filter(task => task.status === 'realisee')
  }, [tasks])

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-cyan-50">
        <PageSkeleton variant="detail" />
      </div>
    )
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-cyan-50 flex items-center justify-center">
        <p className="text-gray-500">Projet non trouvé</p>
      </div>
    )
  }

  const statusStyle = getProjectStatusStyle(project.status)
  const stackTags = getStackTags(project.stack_technique)
  const developers = getDevelopers(project.noms_devs)
  const githubUrls = getUrlsByType(project.urls, 'github')
  const serverUrls = getUrlsByType(project.urls, 'server')
  const figmaUrls = getUrlsByType(project.urls, 'figma')
  const notionUrls = getUrlsByType(project.urls, 'notion')

  // Handle task creation (fichier envoyé en binaire via FormData, pas en base64)
  const handleCreateTask = async (data: TaskFormData) => {
    if (!projectId) return
    setIsCreatingTask(true)
    try {
      const form = new FormData()
      form.append('titre', data.title)
      form.append('description', data.description ?? '')
      form.append('description_client', data.descriptionClient ?? '')
      if (data.type) {
        form.append('type', data.type)
      }
      form.append('status', 'a_effectuer')
      form.append('visibilite_client', data.visibleToClient ? 'true' : 'false')
      if (data.deadline?.trim()) {
        form.append('deadline', data.deadline.trim())
      }
      form.append('deadline_visible_client', data.deadlineVisibleToClient ? 'true' : 'false')
      form.append('requires_prod_test', data.requiresProdTest ? 'true' : 'false')
      if (data.image) {
        form.append('image', data.image, data.image.name)
      }
      await api.postForm<Task>(`/projects/${projectId}/tasks`, form)
      // Refresh tasks
      const response = await api.get<{ data: Task[]; count: number; message: string }>(`/projects/${projectId}/tasks`)
      setTasks(response.data.data || [])
      setShowTaskModal(false)
    } catch (error: unknown) {
      console.error('Erreur lors de la création de la tâche:', error)
      // ApiError.response = body FastAPI { detail: string }
      const err = error as { message?: string; response?: { detail?: string } }
      const detail = typeof err.response?.detail === 'string' ? err.response.detail : err.message
      if (detail && String(detail).includes('Spaces')) {
        alert(`Image non enregistrée : ${detail}\n\nRemplissez SPACES_KEY et SPACES_SECRET dans backend/.env puis redémarrez le backend.`)
      } else if (detail) {
        alert(`Erreur : ${detail}`)
      }
    } finally {
      setIsCreatingTask(false)
    }
  }

  const needsProdTestConfirmation = (task: Task, targetStatus: 'a_effectuer' | 'realisee') =>
    task.status === 'a_effectuer' &&
    targetStatus === 'realisee' &&
    Boolean(task.requires_prod_test)

  const executeToggleTaskStatus = async (taskId: number) => {
    if (!projectId || updatingTaskId === taskId) return
    const task = tasks.find(t => t.id === taskId)
    if (!task) return

    setUpdatingTaskId(taskId)
    try {
      const newStatus = task.status === 'a_effectuer' ? 'realisee' : 'a_effectuer'
      const form = new FormData()
      form.append('status', newStatus)
      await api.putForm<Task>(`/projects/${projectId}/tasks/${taskId}`, form)

      // If task is now "realisee", move it to the top of the completed list
      if (newStatus === 'realisee') {
        // Wait a bit to ensure status change is committed to database
        await new Promise(resolve => setTimeout(resolve, 200))

        // Get all tasks after status change
        const response = await api.get<{ data: Task[]; count: number; message: string }>(`/projects/${projectId}/tasks`)
        const allTasks = response.data.data || []

        // Get all completed tasks, sorted by current ordre (ascending: 0 is top)
        const completedTasks = allTasks
          .filter(t => t.status === 'realisee')
          .sort((a, b) => a.ordre - b.ordre)

        // Verify the newly completed task is in the list
        const newlyCompletedTask = completedTasks.find(t => t.id === taskId)
        if (newlyCompletedTask && completedTasks.length > 0) {
          // Since sort is ascending (a.ordre - b.ordre), order 0 should be at top
          // Put new task FIRST in array to get order 0 (top position)
          const otherCompletedTasks = completedTasks.filter(t => t.id !== taskId)
          const reorderedTaskIds = [taskId, ...otherCompletedTasks.map(t => t.id)]

          // Reorder: API assigns order based on array index (0, 1, 2, ...)
          // First task gets order 0, which should be at top since sort is ascending
          await api.put(`/projects/${projectId}/tasks/reorder`, {
            task_ids: reorderedTaskIds,
            status: 'realisee'
          })

          // Wait a bit for reorder to be committed
          await new Promise(resolve => setTimeout(resolve, 100))
        }
      }

      // Refresh tasks to get the updated order
      const refreshResponse = await api.get<{ data: Task[]; count: number; message: string }>(`/projects/${projectId}/tasks`)
      setTasks(refreshResponse.data.data || [])
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la tâche:', error)
    } finally {
      setUpdatingTaskId(null)
    }
  }

  const executeMoveTask = async (
    taskId: number,
    newStatus: 'a_effectuer' | 'realisee',
    newIndex: number
  ) => {
    if (!projectId) return
    try {
      await api.put(`/projects/${projectId}/tasks/${taskId}`, {
        status: newStatus
      })

      const response = await api.get<{ data: Task[]; count: number; message: string }>(`/projects/${projectId}/tasks`)
      const allTasks = response.data.data || []

      const tasksInNewStatus = allTasks
        .filter(t => t.status === newStatus)
        .sort((a, b) => a.ordre - b.ordre)

      const movedTaskIndex = tasksInNewStatus.findIndex(t => t.id === taskId)
      if (movedTaskIndex !== -1) {
        const otherTasks = tasksInNewStatus.filter(t => t.id !== taskId)
        const reorderedTasks = [
          ...otherTasks.slice(0, newIndex),
          tasksInNewStatus[movedTaskIndex],
          ...otherTasks.slice(newIndex)
        ]

        const taskIds = reorderedTasks.map(t => t.id)
        if (taskIds.length > 0) {
          await api.put(`/projects/${projectId}/tasks/reorder`, {
            task_ids: taskIds,
            status: newStatus
          })
        }
      }

      const refreshResponse = await api.get<{ data: Task[]; count: number; message: string }>(`/projects/${projectId}/tasks`)
      setTasks(refreshResponse.data.data || [])
    } catch (error) {
      console.error('Erreur lors du déplacement de la tâche:', error)
    }
  }

  const requestToggleTaskStatus = (taskId: number) => {
    if (!projectId || updatingTaskId === taskId) return
    const task = tasks.find(t => t.id === taskId)
    if (!task) return
    const newStatus = task.status === 'a_effectuer' ? 'realisee' : 'a_effectuer'
    if (needsProdTestConfirmation(task, newStatus)) {
      setProdTestConfirm({ type: 'toggle', taskId })
      return
    }
    void executeToggleTaskStatus(taskId)
  }

  const requestMoveTask = async (
    taskId: number,
    newStatus: 'a_effectuer' | 'realisee',
    newIndex: number
  ) => {
    const task = tasks.find(t => t.id === taskId)
    if (!task) return
    if (needsProdTestConfirmation(task, newStatus)) {
      setProdTestConfirm({ type: 'move', taskId, newStatus, newIndex })
      return
    }
    await executeMoveTask(taskId, newStatus, newIndex)
  }

  const handleProdTestConfirm = async () => {
    if (!prodTestConfirm) return
    const action = prodTestConfirm
    setProdTestConfirm(null)
    if (action.type === 'toggle') {
      await executeToggleTaskStatus(action.taskId)
    } else {
      await executeMoveTask(action.taskId, action.newStatus, action.newIndex)
    }
  }

  // Handle task status toggle
  const handleToggleTaskStatus = async (taskId: number) => {
    requestToggleTaskStatus(taskId)
  }

  // Handle task deletion
  const handleDeleteTask = async () => {
    if (!projectId || !taskToDelete) return

    setIsDeletingTask(true)
    try {
      await api.delete(`/projects/${projectId}/tasks/${taskToDelete.id}`)
      // Refresh tasks
      const response = await api.get<{ data: Task[]; count: number; message: string }>(`/projects/${projectId}/tasks`)
      setTasks(response.data.data || [])
      setTaskToDelete(null)
    } catch (error) {
      console.error('Erreur lors de la suppression de la tâche:', error)
    } finally {
      setIsDeletingTask(false)
    }
  }

  // Handle task edit
  const handleEditTask = (task: Task) => {
    setTaskToEdit({
      id: task.id,
      title: task.titre,
      description: task.description || '',
      descriptionClient: task.description_client || '',
      type: (task.type === 'Ajustement' ? 'Ajustement' : task.type === 'Feature' ? 'Feature' : '') as 'Feature' | 'Ajustement' | '',
      image: null,
      imageUrl: task.image_url ?? null,
      visibleToClient: task.visibilite_client ?? true,
      deadline: task.deadline
        ? new Date(task.deadline).toISOString().split('T')[0]
        : '',
      deadlineVisibleToClient: task.deadline_visible_client ?? true,
      requiresProdTest: task.requires_prod_test ?? false,
      createdByEmail: task.created_by_email,
    })
    setShowEditTaskModal(true)
  }

  // Handle task update (fichier envoyé en binaire via FormData, pas en base64)
  const handleUpdateTask = async (data: TaskEditData) => {
    if (!projectId) return
    setIsUpdatingTask(true)
    try {
      const form = new FormData()
      form.append('titre', data.title)
      form.append('description', data.description ?? '')
      form.append('description_client', data.descriptionClient ?? '')
      form.append('type', data.type ?? '')
      form.append('visibilite_client', data.visibleToClient ? 'true' : 'false')
      form.append('deadline', data.deadline ?? '')
      form.append('deadline_visible_client', data.deadlineVisibleToClient ? 'true' : 'false')
      form.append('requires_prod_test', data.requiresProdTest ? 'true' : 'false')
      if (data.removeImage) {
        form.append('remove_image', 'true')
      }
      if (data.image) {
        form.append('image', data.image, data.image.name)
      }
      await api.putForm(`/projects/${projectId}/tasks/${data.id}`, form)
      // Refresh tasks
      const response = await api.get<{ data: Task[]; count: number; message: string }>(`/projects/${projectId}/tasks`)
      setTasks(response.data.data || [])
      setShowEditTaskModal(false)
      setTaskToEdit(null)
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la tâche:', error)
    } finally {
      setIsUpdatingTask(false)
    }
  }

  // Handle metric creation
  const handleCreateMetric = async (data: MetricFormData) => {
    if (!projectId) return
    setIsCreatingMetric(true)
    try {
      await api.post<Metric>(`/projects/${projectId}/metrics`, {
        nom: data.name,
        description: data.description || null,
        unite: data.unit || null,
        mode_calcul: data.calculationMode === 'incremental' ? 'incremental' : 'ecrasement',
        visibilite_client: data.visibleToClient
      })
      // Refresh metrics
      const response = await api.get<{ data: Metric[]; count: number; message: string }>(`/projects/${projectId}/metrics`)
      setMetrics(response.data.data || [])
      setShowMetricModal(false)
    } catch (error) {
      console.error('Erreur lors de la création de la métrique:', error)
    } finally {
      setIsCreatingMetric(false)
    }
  }

  // Handle metric edit
  const handleEditMetric = (metric: Metric) => {
    setMetricToEdit({
      id: metric.id,
      name: metric.nom,
      description: metric.description || '',
      unit: metric.unite || '',
      visibleToClient: metric.visibilite_client,
      calculationMode: metric.mode_calcul === 'ecrasement' ? 'overwrite' : 'incremental',
    })
    setShowEditMetricModal(true)
  }

  // Handle metric update
  const handleUpdateMetric = async (data: MetricEditData) => {
    if (!projectId) return
    setIsUpdatingMetric(true)
    try {
      await api.put(`/projects/${projectId}/metrics/${data.id}`, {
        nom: data.name,
        description: data.description || null,
        unite: data.unit || null,
        visibilite_client: data.visibleToClient
      })
      // Refresh metrics
      const response = await api.get<{ data: Metric[]; count: number; message: string }>(`/projects/${projectId}/metrics`)
      setMetrics(response.data.data || [])
      setShowEditMetricModal(false)
      setMetricToEdit(null)
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la métrique:', error)
    } finally {
      setIsUpdatingMetric(false)
    }
  }

  // Handle metric deletion
  const handleDeleteMetric = async () => {
    if (!projectId || !metricToDelete) return
    setIsDeletingMetric(true)
    try {
      await api.delete(`/projects/${projectId}/metrics/${metricToDelete.id}`)
      // Refresh metrics
      const response = await api.get<{ data: Metric[]; count: number; message: string }>(`/projects/${projectId}/metrics`)
      setMetrics(response.data.data || [])
      setMetricToDelete(null)
    } catch (error) {
      console.error('Erreur lors de la suppression de la métrique:', error)
    } finally {
      setIsDeletingMetric(false)
    }
  }

  // Handle roadmap step creation
  const handleCreateRoadmapStep = async (data: RoadmapStepFormData) => {
    if (!projectId) return
    setIsCreatingRoadmapStep(true)
    try {
      await api.post<RoadmapStep>(`/projects/${projectId}/roadmap`, {
        titre: data.title,
        description: data.description || null,
        date: data.date,
        visibilite_client: data.visibleToClient
      })
      // Refresh roadmap steps
      const response = await api.get<{ data: RoadmapStep[]; count: number; message: string }>(`/projects/${projectId}/roadmap`)
      setRoadmapSteps(response.data.data || [])
      setShowRoadmapModal(false)
    } catch (error) {
      console.error('Erreur lors de la création de l\'étape de roadmap:', error)
    } finally {
      setIsCreatingRoadmapStep(false)
    }
  }

  // Handle roadmap step edit
  const handleEditRoadmapStep = (step: RoadmapStep) => {
    // Ensure date is in YYYY-MM-DD format for the date input
    const formattedDate = step.date.split('T')[0]
    setRoadmapStepToEdit({
      id: step.id,
      title: step.titre,
      description: step.description || '',
      date: formattedDate,
      visibleToClient: step.visibilite_client ?? true,
    })
    setShowEditRoadmapModal(true)
  }

  // Handle roadmap step update
  const handleUpdateRoadmapStep = async (data: RoadmapStepEditData) => {
    if (!projectId) return
    setIsUpdatingRoadmapStep(true)
    try {
      // Ensure date is in YYYY-MM-DD format
      const formattedDate = data.date.split('T')[0]
      const payload = {
        titre: data.title,
        description: data.description || null,
        date: formattedDate,
        visibilite_client: data.visibleToClient
      }
      console.log('Sending roadmap update:', payload)
      await api.put(`/projects/${projectId}/roadmap/${data.id}`, payload)
      // Refresh roadmap steps
      const response = await api.get<{ data: RoadmapStep[]; count: number; message: string }>(`/projects/${projectId}/roadmap`)
      setRoadmapSteps(response.data.data || [])
      setShowEditRoadmapModal(false)
      setRoadmapStepToEdit(null)
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour de l\'étape de roadmap:', error)
      console.error('Response data:', error?.response?.data)
      alert(`Erreur: ${error?.response?.data?.detail || error?.message || 'Erreur inconnue'}`)
    } finally {
      setIsUpdatingRoadmapStep(false)
    }
  }

  // Handle roadmap step deletion
  const handleDeleteRoadmapStep = async () => {
    if (!projectId || !roadmapStepToDelete) return
    setIsDeletingRoadmapStep(true)
    try {
      await api.delete(`/projects/${projectId}/roadmap/${roadmapStepToDelete.id}`)
      // Refresh roadmap steps
      const response = await api.get<{ data: RoadmapStep[]; count: number; message: string }>(`/projects/${projectId}/roadmap`)
      setRoadmapSteps(response.data.data || [])
      setRoadmapStepToDelete(null)
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'étape de roadmap:', error)
    } finally {
      setIsDeletingRoadmapStep(false)
    }
  }

  // Handle maintenance creation
  const handleCreateMaintenance = async (data: MaintenanceFormData) => {
    if (!projectId) return
    setIsCreatingMaintenance(true)
    try {
      await api.post<Maintenance>(`/projects/${projectId}/maintenance`, {
        titre: data.titre,
        description: data.description || null,
        date: data.date,
        temps_heures: data.temps_heures || null,
      })
      // Refresh maintenances
      const response = await api.get<{ data: Maintenance[]; count: number; message: string }>(`/projects/${projectId}/maintenance`)
      setMaintenances(response.data.data || [])
      setShowMaintenanceModal(false)
    } catch (error) {
      console.error('Erreur lors de la création de la maintenance:', error)
    } finally {
      setIsCreatingMaintenance(false)
    }
  }

  // Handle maintenance edit
  const handleEditMaintenance = (maintenance: Maintenance) => {
    // Ensure date is in YYYY-MM-DD format for the date input
    const formattedDate = maintenance.date.split('T')[0]
    setMaintenanceToEdit({
      id: maintenance.id,
      titre: maintenance.titre,
      description: maintenance.description || '',
      date: formattedDate,
      temps_heures: maintenance.temps_heures ?? undefined,
    })
    setShowEditMaintenanceModal(true)
  }

  // Handle maintenance update
  const handleUpdateMaintenance = async (data: MaintenanceEditData) => {
    if (!projectId) return
    setIsUpdatingMaintenance(true)
    try {
      // Ensure date is in YYYY-MM-DD format
      const formattedDate = data.date.split('T')[0]
      await api.put(`/projects/${projectId}/maintenance/${data.id}`, {
        titre: data.titre,
        description: data.description || null,
        date: formattedDate,
        temps_heures: data.temps_heures || null,
      })
      // Refresh maintenances
      const response = await api.get<{ data: Maintenance[]; count: number; message: string }>(`/projects/${projectId}/maintenance`)
      setMaintenances(response.data.data || [])
      setShowEditMaintenanceModal(false)
      setMaintenanceToEdit(null)
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la maintenance:', error)
    } finally {
      setIsUpdatingMaintenance(false)
    }
  }

  // Handle maintenance deletion
  const handleDeleteMaintenance = async () => {
    if (!projectId || !maintenanceToDelete) return
    setIsDeletingMaintenance(true)
    try {
      await api.delete(`/projects/${projectId}/maintenance/${maintenanceToDelete.id}`)
      // Refresh maintenances
      const response = await api.get<{ data: Maintenance[]; count: number; message: string }>(`/projects/${projectId}/maintenance`)
      setMaintenances(response.data.data || [])
      setMaintenanceToDelete(null)
    } catch (error) {
      console.error('Erreur lors de la suppression de la maintenance:', error)
    } finally {
      setIsDeletingMaintenance(false)
    }
  }

  // Handle delete project (soft delete)
  const handleDeleteProject = async () => {
    if (!projectId) return
    setIsDeletingProject(true)
    try {
      await api.delete(`/projects/${projectId}`)
      // Navigate back to projects list
      navigate({ to: '/projects' })
    } catch (error) {
      console.error('Erreur lors de la suppression du projet:', error)
    } finally {
      setIsDeletingProject(false)
    }
  }

  // Handle client email update success
  const handleClientEmailUpdateSuccess = async () => {
    if (!projectId) return
    try {
      // Refresh project to get updated client email
      const response = await api.get<ProjectDetail>(`/projects/${projectId}`)
      setProject(response.data)
    } catch (error) {
      console.error('Erreur lors de la récupération du projet:', error)
    }
  }

  // Handle project manager update success
  const handleProjectManagerUpdateSuccess = async () => {
    if (!projectId) return
    try {
      // Refresh project to get updated project manager info
      const response = await api.get<ProjectDetail>(`/projects/${projectId}`)
      setProject(response.data)
    } catch (error) {
      console.error('Erreur lors de la récupération du projet:', error)
    }
  }

  // Handle financial info update success
  const handleFinancialInfoUpdateSuccess = async () => {
    if (!projectId) return
    try {
      const response = await api.get<ProjectDetail>(`/projects/${projectId}`)
      setProject(response.data)
    } catch (error) {
      console.error('Erreur lors de la récupération du projet:', error)
    }
  }

  const handleClientViewConfigSuccess = async () => {
    if (!projectId) return
    try {
      const params = isAdmin && viewMode === 'client' ? { view_mode: 'client' } : undefined
      const response = await api.get<ProjectDetail>(`/projects/${projectId}`, params)
      setProject(response.data)
    } catch (error) {
      console.error('Erreur lors de la récupération du projet:', error)
    }
  }

  // Handle project update
  const handleUpdateProject = async (data: ProjectFormData) => {
    if (!projectId || !project) return
    setIsUpdatingProject(true)
    try {
      // Transform form data to match backend API payload
      const payload: any = {}

      // Add fields only if they have values or are required
      if (data.nomProjet?.trim()) {
        payload.nom = data.nomProjet.trim()
      }
      if (data.dateKickoff) {
        payload.date_kickoff = data.dateKickoff
      }
      if (data.description?.trim()) {
        payload.description = data.description.trim()
      } else if (data.description === '') {
        payload.description = null
      }
      // Handle description_interne (only for super admins)
      if (isSuperAdmin) {
        if (data.descriptionInterne?.trim()) {
          payload.description_interne = data.descriptionInterne.trim()
        } else         if (data.descriptionInterne === '') {
          payload.description_interne = null
        }
      }
      if (data.stackTechnique?.trim()) {
        payload.stack_technique = data.stackTechnique.trim()
      } else if (data.stackTechnique === '') {
        payload.stack_technique = null
      }
      if (data.developerIds) {
        payload.developer_ids = data.developerIds
      } else if (data.developpeurs?.trim()) {
        payload.noms_devs = data.developpeurs.trim()
      } else if (data.developpeurs === '') {
        payload.noms_devs = null
      }
      if (data.status) {
        payload.status = data.status
      }
      if (data.clientId) {
        payload.client_id = data.clientId
      }
      if (data.projectManagerIds?.length) {
        payload.project_manager_ids = data.projectManagerIds
        payload.project_manager_id = data.projectManagerIds[0]
      } else if (data.projectManagerId) {
        payload.project_manager_id = data.projectManagerId
      }
      if (data.activitySectorId && data.activitySectorId.trim() !== '') {
        const sectorId = parseInt(data.activitySectorId)
        if (!isNaN(sectorId) && sectorId > 0) {
          payload.activity_sector_id = sectorId
        }
      } else if (data.activitySectorId === '') {
        payload.activity_sector_id = null
      }

      // Handle URLs - use arrays from the modal if provided, otherwise fallback to single URLs
      if (data.githubUrls !== undefined) {
        payload.github_urls = data.githubUrls
      } else if (data.urlGithub?.trim()) {
        payload.github_urls = [data.urlGithub.trim()]
      } else {
        payload.github_urls = []
      }

      if (data.serverUrls !== undefined) {
        payload.server_urls = data.serverUrls
      } else if (data.urlServeur?.trim()) {
        payload.server_urls = [data.urlServeur.trim()]
      } else {
        payload.server_urls = []
      }

      if (data.figmaUrls !== undefined) {
        payload.figma_urls = data.figmaUrls
      } else if (data.urlFigma?.trim()) {
        payload.figma_urls = [data.urlFigma.trim()]
      } else {
        payload.figma_urls = []
      }

      if (data.notionUrls !== undefined) {
        payload.notion_urls = data.notionUrls
      } else {
        payload.notion_urls = []
      }

      console.log('Payload de mise à jour:', JSON.stringify(payload, null, 2))

      // Ajout de nouveaux emails au client : on fusionne avec les emails actuels
      // (l'endpoint remplace la liste complète et supprimerait les comptes absents)
      if (data.newClientEmails && data.newClientEmails.length > 0 && data.clientNom) {
        const clientPath = `/users/client/${encodeURIComponent(data.clientNom)}/emails`
        const current = await api.get<{ data: Array<{ email: string }>; count: number }>(clientPath)
        const currentEmails = (current.data.data || []).map(u => u.email.toLowerCase())
        const merged = Array.from(new Set([
          ...currentEmails,
          ...data.newClientEmails.map(e => e.trim().toLowerCase()),
        ]))
        if (merged.length > currentEmails.length) {
          await api.put(clientPath, { emails: merged })
        }
      }

      // Make API call to update
      await api.put<ProjectDetail>(`/projects/${projectId}`, payload)
      console.log('Projet mis à jour avec succès')

      // Fetch updated project with all enriched data (client_nom, project_manager_nom, etc.)
      const updatedResponse = await api.get<ProjectDetail>(`/projects/${projectId}`)
      console.log('Projet récupéré après mise à jour:', updatedResponse.data)

      // Update local project state with complete data
      setProject(updatedResponse.data)

      // Close modal on success
      setShowUpdateModal(false)
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour du projet:', error)
      console.error('Détails de l\'erreur:', error?.response?.data)
      // Show error message to user
      let errorMessage = 'Une erreur s\'est produite lors de la mise à jour du projet'
      if (error?.message) {
        errorMessage = error.message
      } else if (error?.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map((d: any) => {
            const field = Array.isArray(d.loc) ? d.loc.slice(-1)[0] : ''
            return field ? `${field}: ${d.msg || d.message || 'Erreur de validation'}` : (d.msg || d.message || 'Erreur de validation')
          }).join(', ')
        } else {
          errorMessage = error.response.data.detail
        }
      }
      alert(errorMessage) // TODO: Replace with proper notification component
    } finally {
      setIsUpdatingProject(false)
    }
  }

  return (
    <div className="min-h-screen">
      <HeroBanner className="pb-10 pt-7" ringsClassName="left-[38%] right-auto -top-16">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <nav aria-label="Fil d'Ariane" className="min-w-0 truncate text-sm">
            <Link to="/projects" className="text-[#8FE3EE] transition-colors hover:text-white">
              Mes projets
            </Link>
            <span className="mx-1.5 text-[#6B7A99]" aria-hidden>/</span>
            <span className="text-[#C3CCE0]">{project.nom}</span>
          </nav>

          {isAdmin && (
            <div className="flex flex-wrap items-center gap-2">
              {effectiveViewMode === 'admin' && (
                <button
                  onClick={() => setShowClientViewConfigModal(true)}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/20 bg-white/10 text-white transition-colors hover:bg-white/20"
                  title="Configuration vue client"
                  aria-label="Configuration vue client"
                >
                  <Eye className="w-4 h-4" />
                </button>
              )}
              <div role="group" aria-label="Mode d'affichage" className="flex rounded-lg bg-white/10 p-1">
                <button
                  type="button"
                  onClick={() => setViewMode('admin')}
                  aria-pressed={viewMode === 'admin'}
                  className={`rounded-md px-3 py-1 text-sm font-medium transition-colors ${viewMode === 'admin' ? 'bg-white text-brand-navy' : 'text-white/80 hover:text-white'}`}
                >
                  Vue admin
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('client')}
                  aria-pressed={viewMode === 'client'}
                  className={`rounded-md px-3 py-1 text-sm font-medium transition-colors ${viewMode === 'client' ? 'bg-white text-brand-navy' : 'text-white/80 hover:text-white'}`}
                >
                  Vue client
                </button>
              </div>
              {effectiveViewMode === 'admin' && (
                <>
                  <button
                    onClick={() =>
                      navigate({
                        to: '/projects/audit/$projectId',
                        params: { projectId: String(projectId) },
                      })
                    }
                    className="inline-flex h-9 items-center gap-2 rounded-lg bg-brand-bright px-3 text-sm font-medium text-brand-navy transition-colors hover:bg-[#5FD3E3]"
                  >
                    <ScrollText className="w-4 h-4" />
                    Journal d’audit
                  </button>
                  <button
                    onClick={() => setShowUpdateModal(true)}
                    className="inline-flex h-9 items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-3 text-sm font-medium text-white transition-colors hover:bg-white/20"
                  >
                    <Edit className="w-4 h-4" />
                    Modifier
                  </button>
                  <button
                    onClick={() => setShowDeleteProjectModal(true)}
                    className="inline-flex h-9 items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-3 text-sm font-medium text-white transition-colors hover:border-red-300 hover:bg-red-500/20"
                  >
                    <Trash2 className="w-4 h-4" />
                    Supprimer
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        <div className="mt-6 flex flex-wrap items-start gap-8">
          <div className="min-w-0 flex-[3_1_480px] space-y-3.5">
            <div className="flex items-center gap-2 text-sm font-medium text-[#8FE3EE]">
              <Building2 className="w-4 h-4" />
              <span>Client · {project.client_nom || 'Non spécifié'}</span>
              {effectiveViewMode === 'admin' && project.client_id && (
                <button
                  onClick={() => setShowEditClientEmailModal(true)}
                  className="rounded p-1 text-[#8FE3EE] transition-colors hover:bg-white/10 hover:text-white"
                  title="Modifier le client"
                  aria-label="Modifier le client"
                >
                  <Edit className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">{project.nom}</h1>
              <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${statusStyle.pill}`}>
                <span className={`h-[7px] w-[7px] rounded-full ${statusStyle.dot}`} />
                {statusStyle.label}
              </span>
              {effectiveViewMode === 'admin' && isSuperAdmin && project.visibility === 'interne' && (
                <span className="inline-flex rounded-full bg-violet-100 px-2.5 py-0.5 text-xs font-semibold text-violet-800">
                  Interne
                </span>
              )}
            </div>
            <p className={`whitespace-pre-line text-[15px] leading-relaxed ${project.description ? 'text-[#D3DAEA]' : 'text-[#8A96B3]'}`}>
              {project.description || 'Aucune description'}
            </p>

            {/* Description interne - visible uniquement pour les super admins en vue admin */}
            {effectiveViewMode === 'admin' && isSuperAdmin && project.description_interne && (
              <div className="rounded-xl border border-white/15 bg-white/5 p-4">
                <div className="mb-2 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-brand-bright" />
                  <h3 className="text-sm font-semibold text-white">Description interne</h3>
                </div>
                <p className="whitespace-pre-wrap text-sm text-[#D3DAEA]">{project.description_interne}</p>
              </div>
            )}
          </div>

          <div className="min-w-0 flex-[2_1_300px] rounded-[18px] bg-white px-5 py-1.5 text-brand-navy shadow-xl shadow-black/10">
            <dl className="divide-y divide-gray-100">
              <InfoRow icon={<Calendar className="w-4 h-4 text-brand" />} label="Date de kickoff">
                {formatDate(project.date_kickoff)}
              </InfoRow>
              <InfoRow
                icon={<User className="w-4 h-4 text-brand" />}
                label={(project.project_managers?.length ?? 0) > 1 ? 'Chefs de projet' : 'Chef de projet'}
              >
                <div className="flex flex-col items-end gap-1.5">
                <div className="flex items-center justify-end gap-1">
                  <div className="min-w-0 text-right">
                    <div>{project.project_manager_nom || 'Non spécifié'}</div>
                    {project.project_manager_email && (
                      <a
                        href={`mailto:${project.project_manager_email}`}
                        className="block truncate text-xs font-normal text-brand hover:text-brand-dark"
                      >
                        {project.project_manager_email}
                      </a>
                    )}
                  </div>
                  {effectiveViewMode === 'admin' && project.project_manager_id && (
                    <button
                      onClick={() => setShowEditProjectManagerModal(true)}
                      className="ml-1 rounded p-1 text-gray-400 transition-colors hover:bg-brand-light hover:text-brand"
                      title="Modifier le chef de projet"
                      aria-label="Modifier le chef de projet"
                    >
                      <Edit className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                {(project.project_managers ?? [])
                  .filter((pm) => pm.id !== project.project_manager_id)
                  .map((pm) => (
                    <div key={pm.id} className="min-w-0 text-right">
                      <div>{pm.nom || pm.email || 'Chef de projet'}</div>
                      {pm.email && (
                        <a
                          href={`mailto:${pm.email}`}
                          className="block truncate text-xs font-normal text-brand hover:text-brand-dark"
                        >
                          {pm.email}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </InfoRow>
              {(effectiveViewMode === 'admin' || project.activity_sector_nom) && (
                <InfoRow
                  icon={<Building2 className="w-4 h-4 text-brand-violet" />}
                  iconBg="bg-brand-violet-light"
                  label="Secteur d'activité"
                >
                  {project.activity_sector_nom || 'Non spécifié'}
                </InfoRow>
              )}
              {/* Développeurs - visible uniquement en vue admin */}
              {effectiveViewMode === 'admin' && (
                <InfoRow icon={<Users className="w-4 h-4 text-gray-600" />} iconBg="bg-gray-100" label="Développeurs">
                  {developers.length > 0 ? (
                    <div className="flex flex-wrap justify-end gap-1">
                      {developers.map((dev, index) => (
                        <span key={index} className="rounded-md bg-gray-100 px-2 py-0.5 text-xs font-normal text-gray-700">
                          {dev}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="font-normal text-gray-500">Aucun développeur assigné</span>
                  )}
                </InfoRow>
              )}
              {(effectiveViewMode === 'admin' || stackTags.length > 0) && (
                <InfoRow icon={<Code className="w-4 h-4 text-amber-700" />} iconBg="bg-[#FFF1DC]" label="Stack technique">
                  {stackTags.length > 0 ? (
                    <div className="flex flex-wrap justify-end gap-1">
                      {stackTags.map((tech, index) => (
                        <span key={index} className="rounded-md bg-brand-light px-2 py-0.5 text-xs font-normal text-brand-dark">
                          {tech}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="font-normal text-gray-500">Aucune stack technique spécifiée</span>
                  )}
                </InfoRow>
              )}
              {effectiveViewMode === 'admin' && githubUrls.length > 0 && (
                <UrlListRow
                  icon={<Github className="w-4 h-4 text-gray-700" />}
                  label={`Repository GitHub${githubUrls.length > 1 ? ` (${githubUrls.length})` : ''}`}
                  urls={githubUrls}
                />
              )}
              {effectiveViewMode === 'admin' && serverUrls.length > 0 && (
                <UrlListRow
                  icon={<Server className="w-4 h-4 text-gray-700" />}
                  label={`Environnement serveur${serverUrls.length > 1 ? ` (${serverUrls.length})` : ''}`}
                  urls={serverUrls}
                />
              )}
              {effectiveViewMode === 'admin' && figmaUrls.length > 0 && (
                <UrlListRow
                  icon={<Figma className="w-4 h-4 text-brand-violet" />}
                  label={`Schéma technique Figma${figmaUrls.length > 1 ? ` (${figmaUrls.length})` : ''}`}
                  urls={figmaUrls}
                />
              )}
              {effectiveViewMode === 'admin' && notionUrls.length > 0 && (
                <UrlListRow
                  icon={<BookOpen className="w-4 h-4 text-amber-700" />}
                  label={`Documentation Notion${notionUrls.length > 1 ? ` (${notionUrls.length})` : ''}`}
                  urls={notionUrls}
                />
              )}
            </dl>
            {/* Liens Figma / Notion en boutons pour le client */}
            {effectiveViewMode === 'client' && (figmaUrls.length > 0 || notionUrls.length > 0) && (
              <div className="flex flex-wrap gap-2 border-t border-gray-100 py-3.5">
                {figmaUrls.map((url, index) => (
                  <a
                    key={`figma-${index}`}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex min-h-[42px] flex-1 items-center justify-center gap-1.5 rounded-[10px] bg-brand-violet-light px-3 text-sm font-medium text-brand-violet-dark transition-colors hover:bg-[#E6DFFF]"
                  >
                    <Figma className="w-4 h-4" />
                    Maquettes Figma{figmaUrls.length > 1 ? ` ${index + 1}` : ''}
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                ))}
                {notionUrls.map((url, index) => (
                  <a
                    key={`notion-${index}`}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex min-h-[42px] flex-1 items-center justify-center gap-1.5 rounded-[10px] bg-[#FFF4E5] px-3 text-sm font-medium text-[#93530B] transition-colors hover:bg-[#FFEACC]"
                  >
                    <BookOpen className="w-4 h-4" />
                    Documentation{notionUrls.length > 1 ? ` ${index + 1}` : ''}
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </HeroBanner>

      <main className="mx-auto w-full max-w-7xl space-y-8 px-4 pb-16 pt-8 sm:px-8">

        {effectiveViewMode === 'admin' && isSuperAdmin && (
          <FinancialInfoSection
            project={project}
            onEdit={() => setShowEditFinancialModal(true)}
            timeContributorsLoading={loadingTimeContributors}
            timeContributors={timeContributors}
            onVoirPlusTemps={() => setShowTimeContributorsModal(true)}
          />
        )}

        {showMetricsSection && (
        <MetricsSection
          metrics={metrics
            .filter(metric => effectiveViewMode === 'admin' || metric.visibilite_client)
            .map(metric => ({
              id: metric.id,
              name: metric.nom,
              description: metric.description || '',
              currentValue: metric.data && metric.data.length > 0 ? metric.data[metric.data.length - 1].value : '0',
              unit: metric.unite || '',
              visibleToClient: metric.visibilite_client,
              type: metric.mode_calcul === 'ecrasement' ? 'overwrite' : 'incremental',
              endpoint: metric.api_url,
              apiKey: metric.api_token,
              data: metric.data
                .slice()
                .reverse()
                .map((d) => {
                  const fallbackDate = (d as { date?: string }).date
                  const createdAtRaw = d.created_at || fallbackDate || Date.now()
                  return {
                    // Keep both display label and precise date for grouping
                    date: new Date(createdAtRaw).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
                    value: parseFloat(d.value) || 0,
                    created_at: d.created_at ?? fallbackDate
                  }
                })
            }))}
          onNewMetric={() => setShowMetricModal(true)}
          onEditMetric={(metricId) => {
            const metric = metrics.find(m => m.id === metricId)
            if (metric) handleEditMetric(metric)
          }}
          onDeleteMetric={(metricId, metricName) => setMetricToDelete({ id: metricId, name: metricName })}
          sortOrder={metricSortOrder}
          onSortOrderChange={setMetricSortOrder}
          loading={loadingMetrics}
          isAdmin={effectiveViewMode === 'admin'}
        />
        )}

        {showTasksSection && (
        <TasksSection
          tasks={{
            todo: todoTasks
              .filter((task) => effectiveViewMode === 'admin' || (task.visibilite_client ?? true))
              .map(task => ({
              id: task.id,
              title: task.titre,
              description: effectiveViewMode === 'admin' ? (task.description || '') : (task.description_client || ''),
              type: task.type ?? '',
              imageUrl: task.image_url || null,
              createdAt: formatDate(task.date_creation),
              rawDate: task.date_creation,
              ordre: task.ordre,
              visibleToClient: task.visibilite_client ?? true,
              createdByEmail: task.created_by_email,
              deadlineAt:
                task.deadline &&
                (effectiveViewMode === 'admin' || (task.deadline_visible_client ?? true))
                  ? formatDate(task.deadline)
                  : undefined,
            })),
            completed: completedTasks
              .filter((task) => effectiveViewMode === 'admin' || (task.visibilite_client ?? true))
              .map(task => ({
              id: task.id,
              title: task.titre,
              description: effectiveViewMode === 'admin' ? (task.description || '') : (task.description_client || ''),
              type: task.type ?? '',
              imageUrl: task.image_url || null,
              createdAt: formatDate(task.date_creation),
              completedAt: task.date_completion ? formatDate(task.date_completion) : formatDate(task.updated_at || task.created_at),
              rawDate: task.date_completion || task.updated_at || task.created_at,
              ordre: task.ordre,
              visibleToClient: task.visibilite_client ?? true,
              createdByEmail: task.created_by_email,
              completedByEmail: task.completed_by_email,
              deadlineAt:
                task.deadline &&
                (effectiveViewMode === 'admin' || (task.deadline_visible_client ?? true))
                  ? formatDate(task.deadline)
                  : undefined,
            })),
          }}
          onNewTask={() => setShowTaskModal(true)}
          onToggleTask={(taskId) => handleToggleTaskStatus(taskId)}
          onOpenTask={(taskId) => {
            const t = tasks.find((x) => x.id === taskId)
            if (t) setTaskToView(t)
          }}
          onEditTask={(taskId) => {
            const task = tasks.find(t => t.id === taskId)
            if (task) handleEditTask(task)
          }}
          onDeleteTask={(taskId, taskTitle) => setTaskToDelete({ id: taskId, title: taskTitle })}
          onReorderTasks={async (taskIds, status) => {
            if (!projectId) return
            try {
              const payload = {
                task_ids: taskIds,
                status: status
              }
              console.log('Sending reorder request:', payload)
              await api.put(`/projects/${projectId}/tasks/reorder`, payload)
              // Refresh tasks
              const response = await api.get<{ data: Task[]; count: number; message: string }>(`/projects/${projectId}/tasks`)
              setTasks(response.data.data || [])
            } catch (error) {
              console.error('Erreur lors du réordonnancement des tâches:', error)
            }
          }}
          onMoveTask={(taskId, newStatus, newIndex) =>
            requestMoveTask(taskId, newStatus, newIndex)
          }
          loading={loadingTasks}
          updatingTaskId={updatingTaskId}
          isAdmin={effectiveViewMode === 'admin'}
          showTaskUserEmails={effectiveViewMode === 'admin'}
        />
        )}

        {/* Delete Task Modal */}
        <DeleteModal
          open={taskToDelete !== null}
          onOpenChange={(open) => !open && setTaskToDelete(null)}
          onConfirm={handleDeleteTask}
          title="Supprimer la tâche"
          itemName={taskToDelete?.title}
          isLoading={isDeletingTask}
        />

        <ConfirmModal
          open={prodTestConfirm !== null}
          onOpenChange={(open) => !open && setProdTestConfirm(null)}
          onConfirm={handleProdTestConfirm}
          title="Test en production"
          description="Avez-vous bien testé la tâche en production ?"
          confirmLabel="Oui, marquer comme terminée"
        />

        {/* View Task Modal — reste dans le cadre, texte à la ligne */}
        <Dialog open={taskToView !== null} onOpenChange={(open) => !open && setTaskToView(null)}>
          <DialogContent className="max-w-lg max-h-[85vh] flex flex-col overflow-hidden p-6">
            <DialogHeader className="flex-shrink-0 min-w-0">
              <DialogTitle className="pr-8 break-words hyphens-auto">{taskToView?.titre}</DialogTitle>
            </DialogHeader>
            {taskToView && (
              <div className="flex-1 min-h-0 overflow-y-auto space-y-3 min-w-0">
                {taskToView.type && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {taskToView.type === 'Ajustement' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                        <Wrench className="w-3.5 h-3.5" />
                        Ajustement
                      </span>
                    ) : taskToView.type === 'Feature' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-cyan-100 text-cyan-800 border border-cyan-200">
                        <Sparkles className="w-3.5 h-3.5" />
                        Feature
                      </span>
                    ) : null}
                  </div>
                )}
                {effectiveViewMode === 'admin' && taskToView.description ? (
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-500 mb-1">Description interne</p>
                    <TextWithLinks
                      text={taskToView.description}
                      className="text-sm text-gray-700 min-w-0"
                    />
                  </div>
                ) : null}
                {(effectiveViewMode === 'admin' ? taskToView.description_client : (taskToView.description_client || taskToView.description)) ? (
                  <div className="min-w-0">
                    {effectiveViewMode === 'admin' ? (
                      <p className="text-xs font-medium text-gray-500 mb-1">Description visible par le client</p>
                    ) : null}
                    <TextWithLinks
                      text={(effectiveViewMode === 'admin' ? taskToView.description_client : (taskToView.description_client || taskToView.description)) || ''}
                      className="text-sm text-gray-700 min-w-0"
                    />
                  </div>
                ) : null}
                {taskToView.deadline ? (
                  <p className="text-xs text-gray-500">Échéance {formatDate(taskToView.deadline)}</p>
                ) : null}
                {taskToView.image_url && (
                  <div className="rounded-lg overflow-hidden border border-gray-200 bg-gray-50 min-w-0">
                    <img src={taskToView.image_url} alt="" className="w-full h-auto object-contain max-h-64 object-center" />
                  </div>
                )}
                <div className="text-xs text-gray-500 flex-shrink-0 space-y-0.5">
                  <p>
                    Créée le {formatDate(taskToView.date_creation)}
                    {effectiveViewMode === 'admin' && taskToView.created_by_email
                      ? ` par ${taskToView.created_by_email}`
                      : ''}
                  </p>
                  {taskToView.status === 'realisee' && taskToView.date_completion ? (
                    <p>
                      Complétée le {formatDate(taskToView.date_completion)}
                      {effectiveViewMode === 'admin' && taskToView.completed_by_email
                        ? ` par ${taskToView.completed_by_email}`
                        : ''}
                    </p>
                  ) : null}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {showRoadmapSection && (
        <div>
          <RoadmapSection
            steps={roadmapSteps.filter(
              (step) => effectiveViewMode === 'admin' || (step.visibilite_client ?? true)
            )}
            onNewStep={() => setShowRoadmapModal(true)}
            onEditStep={handleEditRoadmapStep}
            onDeleteStep={(stepId, stepTitle) => setRoadmapStepToDelete({ id: stepId, title: stepTitle })}
            sortOrder={roadmapSortOrder}
            onSortOrderChange={setRoadmapSortOrder}
            loading={loadingRoadmap}
            isAdmin={effectiveViewMode === 'admin'}
          />
        </div>
        )}

        {effectiveViewMode === 'admin' && (
          <div>
            <MaintenanceSection
              maintenances={maintenances}
              onNewMaintenance={() => setShowMaintenanceModal(true)}
              onEditMaintenance={handleEditMaintenance}
              onDeleteMaintenance={(maintenanceId, maintenanceTitle) => setMaintenanceToDelete({ id: maintenanceId, title: maintenanceTitle })}
              sortOrder={maintenanceSortOrder}
              onSortOrderChange={setMaintenanceSortOrder}
              loading={loadingMaintenances}
              isAdmin={effectiveViewMode === 'admin'}
            />
          </div>
        )}

        <CreateTaskModal
          open={showTaskModal}
          onOpenChange={setShowTaskModal}
          onSubmit={handleCreateTask}
          isLoading={isCreatingTask}
        />

        <EditTaskModal
          open={showEditTaskModal}
          onOpenChange={setShowEditTaskModal}
          onSubmit={handleUpdateTask}
          isLoading={isUpdatingTask}
          task={taskToEdit}
        />

        <CreateMetricModal
          open={showMetricModal}
          onOpenChange={setShowMetricModal}
          onSubmit={handleCreateMetric}
          isLoading={isCreatingMetric}
        />

        <EditMetricModal
          open={showEditMetricModal}
          onOpenChange={setShowEditMetricModal}
          onSubmit={handleUpdateMetric}
          isLoading={isUpdatingMetric}
          metric={metricToEdit}
        />

        <DeleteModal
          open={metricToDelete !== null}
          onOpenChange={(open) => !open && setMetricToDelete(null)}
          onConfirm={handleDeleteMetric}
          title="Supprimer la métrique"
          itemName={metricToDelete?.name}
          isLoading={isDeletingMetric}
        />

        <CreateRoadmapStepModal
          open={showRoadmapModal}
          onOpenChange={setShowRoadmapModal}
          onSubmit={handleCreateRoadmapStep}
          isLoading={isCreatingRoadmapStep}
        />

        <EditRoadmapStepModal
          open={showEditRoadmapModal}
          onOpenChange={setShowEditRoadmapModal}
          onSubmit={handleUpdateRoadmapStep}
          isLoading={isUpdatingRoadmapStep}
          step={roadmapStepToEdit}
        />

        <DeleteModal
          open={roadmapStepToDelete !== null}
          onOpenChange={(open) => !open && setRoadmapStepToDelete(null)}
          onConfirm={handleDeleteRoadmapStep}
          title="Supprimer l'étape de roadmap"
          itemName={roadmapStepToDelete?.title}
          isLoading={isDeletingRoadmapStep}
        />

        <CreateMaintenanceModal
          open={showMaintenanceModal}
          onOpenChange={setShowMaintenanceModal}
          onSubmit={handleCreateMaintenance}
          isLoading={isCreatingMaintenance}
        />

        <EditMaintenanceModal
          open={showEditMaintenanceModal}
          onOpenChange={setShowEditMaintenanceModal}
          onSubmit={handleUpdateMaintenance}
          isLoading={isUpdatingMaintenance}
          maintenance={maintenanceToEdit}
        />

        <DeleteModal
          open={maintenanceToDelete !== null}
          onOpenChange={(open) => !open && setMaintenanceToDelete(null)}
          onConfirm={handleDeleteMaintenance}
          title="Supprimer la maintenance"
          itemName={maintenanceToDelete?.title}
          isLoading={isDeletingMaintenance}
        />

        {project && (
          <EditClientEmailModal
            open={showEditClientEmailModal}
            onOpenChange={setShowEditClientEmailModal}
            onSuccess={handleClientEmailUpdateSuccess}
            client={project.client_id ? {
              id: project.client_id,
              name: project.client_nom || 'Client',
              email: project.client_email || ''
            } : null}
          />
        )}

        {project && (
          <EditProjectManagerModal
            open={showEditProjectManagerModal}
            onOpenChange={setShowEditProjectManagerModal}
            onSuccess={handleProjectManagerUpdateSuccess}
            projectManager={project.project_manager_id ? {
              id: project.project_manager_id,
              name: project.project_manager_nom || 'Chef de projet',
              email: project.project_manager_email || ''
            } : null}
          />
        )}

        {project && projectId && (
          <>
            <ProjectTimeContributorsModal
              open={showTimeContributorsModal}
              onOpenChange={setShowTimeContributorsModal}
              projectName={project.nom}
              data={timeContributors}
              loading={loadingTimeContributors}
            />

            <EditFinancialInfoModal
              open={showEditFinancialModal}
              onOpenChange={setShowEditFinancialModal}
              onSuccess={handleFinancialInfoUpdateSuccess}
              projectId={projectId}
              initialValues={{
                montant_facture_build: project.montant_facture_build,
                montant_run: project.montant_run,
                couts: project.couts,
                jours_vendu: project.jours_vendu,
                jours_reel: project.jours_reel,
                payment_status_id: project.payment_status_id,
              }}
            />

            {isAdmin && (
            <ClientViewConfigModal
              open={showClientViewConfigModal}
              onOpenChange={setShowClientViewConfigModal}
              onSuccess={handleClientViewConfigSuccess}
              projectId={projectId}
              isSuperAdmin={isSuperAdmin}
              initialValues={{
                tasks_visible_client: project.tasks_visible_client,
                roadmap_visible_client: project.roadmap_visible_client,
                metrics_visible_client: project.metrics_visible_client,
                visibility: project.visibility,
              }}
            />
            )}
          </>
        )}

        {project && (
          <UpdateProjectModal
            open={showUpdateModal}
            onOpenChange={setShowUpdateModal}
            onSubmit={handleUpdateProject}
            isLoading={isUpdatingProject}
            project={{
              id: project.id,
              nom: project.nom,
              description: project.description,
              description_interne: project.description_interne,
              date_kickoff: project.date_kickoff,
              stack_technique: project.stack_technique,
              noms_devs: project.noms_devs,
              developer_ids: project.developer_ids,
              status: project.status,
              visibility: project.visibility,
              client_id: project.client_id,
              project_manager_id: project.project_manager_id,
              project_manager_ids: project.project_manager_ids,
              activity_sector_id: project.activity_sector_id,
              urls: project.urls,
            }}
          />
        )}

        <DeleteModal
          open={showDeleteProjectModal}
          onOpenChange={setShowDeleteProjectModal}
          onConfirm={handleDeleteProject}
          title="Supprimer le projet"
          itemName={project?.nom}
          isLoading={isDeletingProject}
          description="Cette action supprimera le projet. Le projet pourra être restauré ultérieurement si nécessaire."
        />
      </main>
    </div>
  )
}

// --- Informations Financières (visible admin uniquement) ---
function fmtHoursShort(h: number): string {
  return `${Number(h).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} h`
}
function fmtEuro(v: number | null | undefined): string {
  if (v == null) return '—'
  return `${Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €`
}
function fmtJours(v: number | null | undefined): string {
  if (v == null) return '—'
  return `${v} jours`
}

/** Jours réel = ratio (heures / 9 h), affichage avec décimales */
function fmtJoursReel(v: number | null | undefined): string {
  if (v == null) return '—'
  return `${Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 2, minimumFractionDigits: 0 })} jours`
}

interface FinancialInfoSectionProps {
  project: ProjectDetail
  onEdit: () => void
  timeContributorsLoading?: boolean
  timeContributors?: ProjectTimeContributorsData | null
  onVoirPlusTemps?: () => void
}

function FinancialInfoSection({
  project,
  onEdit,
  timeContributorsLoading = false,
  timeContributors = null,
  onVoirPlusTemps,
}: FinancialInfoSectionProps) {
  const contributorsCount = timeContributors?.contributors_count ?? 0
  const totalHours = timeContributors?.total_hours ?? 0
  const ticketDays = timeContributors?.ticket_days_total ?? 0
  const standupIds = new Set((timeContributors?.contributors ?? []).map((person) => person.user_id))
  const ticketOnlyPeople = new Set<string>()
  for (const line of timeContributors?.ticket_times ?? []) {
    if (line.user_id && standupIds.has(line.user_id)) continue
    if (!line.user_id && (timeContributors?.contributors ?? []).some((person) => person.user_nom === line.person_nom)) continue
    ticketOnlyPeople.add(line.user_id || line.person_nom)
  }
  const peopleCount = contributorsCount + ticketOnlyPeople.size
  const mf = project.montant_facture_build
  const co = project.couts
  const jv = project.jours_vendu
  const jr = project.jours_reel

  const margePct = (mf != null && co != null && mf > 0)
    ? ((mf - co) / mf * 100)
    : null
  const margeMontant = (mf != null && co != null) ? (mf - co) : null
  const ecartJours = (jv != null && jr != null) ? (jr - jv) : null
  const ecartLabel = ecartJours != null
    ? (ecartJours <= 0 ? 'Dans les temps' : 'Dépassement')
    : null

  return (
    <div className="bg-white rounded-[18px] border border-gray-200 p-4 sm:p-6">
      <div className="flex items-start justify-between gap-4 mb-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
            <Euro className="w-5 h-5 text-sky-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">Informations Financières</h2>
            <p className="text-sm text-gray-500">Données financières du projet (visible admin uniquement)</p>
          </div>
        </div>
        <button
          onClick={onEdit}
          className="p-2 text-gray-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors"
          title="Modifier les informations financières"
        >
          <Edit className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        <div className="rounded-xl p-4 bg-amber-50 border border-amber-100 flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-medium text-gray-700">État du paiement</div>
            <div className="text-xl font-bold text-gray-900 mt-1">{project.payment_status_nom || '—'}</div>
          </div>
        </div>

        {/* Ligne 1 - fond bleu clair */}
        <div className="rounded-xl p-4 bg-sky-50 flex items-start gap-3">
          <Euro className="w-5 h-5 text-sky-500 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-medium text-gray-700">Montant facturé (Build)</div>
            <div className="text-xl font-bold text-gray-900 mt-1">{fmtEuro(project.montant_facture_build)}</div>
          </div>
        </div>
        <div className="rounded-xl p-4 bg-sky-50 flex items-start gap-3">
          <TrendingUp className="w-5 h-5 text-sky-500 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-medium text-gray-700">Montant de run</div>
            <div className="text-xl font-bold text-gray-900 mt-1">
              {project.montant_run != null ? `${Number(project.montant_run).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €/mois` : '—'}
            </div>
          </div>
        </div>
        <div className="rounded-xl p-4 bg-sky-50 flex items-start gap-3">
          <TrendingUp className="w-5 h-5 text-sky-500 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-medium text-gray-700">Coûts</div>
            <div className="text-xl font-bold text-gray-900 mt-1">{fmtEuro(project.couts)}</div>
          </div>
        </div>

        {/* Ligne 2 - fond blanc */}
        <div className="rounded-xl p-4 bg-white border border-gray-200 flex items-start gap-3">
          <Clock className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-medium text-gray-700">Jours vendu</div>
            <div className="text-xl font-bold text-gray-900 mt-1">{fmtJours(project.jours_vendu)}</div>
          </div>
        </div>
        <div className="rounded-xl p-4 bg-white border border-gray-200 flex items-start gap-3">
          <Calendar className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-medium text-gray-700">Jours réel</div>
            <div className="text-xl font-bold text-gray-900 mt-1">{fmtJoursReel(project.jours_reel)}</div>
          </div>
        </div>
        <div className="rounded-xl p-4 bg-white border border-gray-200 flex items-start gap-3">
          <TrendingUp className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-medium text-gray-700">Marge</div>
            <div className="text-xl font-bold text-gray-900 mt-1">
              {margePct != null ? `${margePct.toFixed(1)} %` : '—'}
            </div>
            {margeMontant != null && <div className="text-sm text-gray-500 mt-0.5">{fmtEuro(margeMontant)}</div>}
          </div>
        </div>

        {/* Ligne 3 — Écart jours */}
        <div className="rounded-xl p-4 bg-green-50 flex items-start gap-3">
          <Clock className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-medium text-gray-700">Écart jours</div>
            <div className="text-xl font-bold text-green-700 mt-1">
              {ecartJours != null ? `${ecartJours >= 0 ? '+' : ''}${ecartJours} jours` : '—'}
            </div>
            {ecartLabel && <div className="text-sm text-green-600 mt-0.5">{ecartLabel}</div>}
          </div>
        </div>

        {/* Ligne 3 — Temps passé (équipe), même format carte */}
        <div className="rounded-xl p-4 bg-violet-50 border border-violet-100 flex items-start gap-3">
          <Users className="w-5 h-5 text-violet-600 flex-shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-gray-700">Temps passé (équipe)</div>
            {timeContributorsLoading ? (
              <div className="text-xl font-bold text-gray-900 mt-1">…</div>
            ) : (
              <>
                <div className="text-xl font-bold text-gray-900 mt-1">
                  {peopleCount}{' '}
                  {peopleCount > 1 ? 'personnes' : 'personne'}
                  {totalHours > 0 ? ` · ${fmtHoursShort(totalHours)}` : ''}
                </div>
              </>
            )}
            <div className={`mt-0.5 flex items-center ${ticketDays > 0 ? 'justify-between gap-3' : ''}`}>
              {ticketDays > 0 && (
                <span className="text-sm text-gray-500">
                  {Number(ticketDays).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} j tickets
                </span>
              )}
              {onVoirPlusTemps && (
                <button
                  type="button"
                  onClick={onVoirPlusTemps}
                  disabled={timeContributorsLoading}
                  className="text-sm font-medium text-violet-700 hover:text-violet-900 disabled:opacity-50"
                >
                  Voir plus →
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

interface InfoRowProps {
  icon: React.ReactNode
  /** Fond de la vignette d'icône */
  iconBg?: string
  label: string
  children: React.ReactNode
}

/** Ligne du panneau d'informations de la fiche projet. */
function InfoRow({ icon, iconBg = 'bg-brand-light', label, children }: InfoRowProps) {
  return (
    <div className="flex items-center gap-3 py-3.5">
      <span className={`flex h-[34px] w-[34px] flex-shrink-0 items-center justify-center rounded-[10px] ${iconBg}`}>
        {icon}
      </span>
      <dt className="flex-shrink-0 text-[13px] text-gray-500">{label}</dt>
      <dd className="m-0 min-w-0 flex-1 text-right text-sm font-medium">{children}</dd>
    </div>
  )
}

/** Ligne listant une ou plusieurs URL (GitHub, serveur, Figma, Notion) en vue admin. */
function UrlListRow({ icon, label, urls }: { icon: React.ReactNode; label: string; urls: string[] }) {
  return (
    <div className="py-3.5">
      <div className="mb-2 flex items-center gap-3">
        <span className="flex h-[34px] w-[34px] flex-shrink-0 items-center justify-center rounded-[10px] bg-gray-100">
          {icon}
        </span>
        <dt className="text-[13px] text-gray-500">{label}</dt>
      </div>
      <dd className="m-0 space-y-1 pl-[46px]">
        {urls.map((url, index) => (
          <a
            key={index}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between gap-2 text-sm text-brand hover:text-brand-dark"
          >
            <span className="truncate">{url}</span>
            <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
          </a>
        ))}
      </dd>
    </div>
  )
}

interface TasksSectionProps {
  tasks: {
    todo: Array<{
      id: number
      title: string
      description: string
      type?: string
      imageUrl?: string | null
      createdAt: string
      rawDate: string
      ordre: number
      visibleToClient?: boolean
      createdByEmail?: string
      deadlineAt?: string
    }>
    completed: Array<{
      id: number
      title: string
      description: string
      type?: string
      imageUrl?: string | null
      createdAt: string
      completedAt: string
      rawDate: string
      ordre: number
      visibleToClient?: boolean
      createdByEmail?: string
      completedByEmail?: string
      deadlineAt?: string
    }>
  }
  onNewTask: () => void
  onToggleTask: (taskId: number) => void
  onOpenTask?: (taskId: number) => void
  onEditTask: (taskId: number) => void
  onDeleteTask: (taskId: number, taskTitle: string) => void
  onReorderTasks: (taskIds: number[], status: 'a_effectuer' | 'realisee') => Promise<void>
  onMoveTask?: (taskId: number, newStatus: 'a_effectuer' | 'realisee', newIndex: number) => Promise<void>
  loading?: boolean
  updatingTaskId?: number | null
  isAdmin?: boolean
  /** Faux en vue client : afficher uniquement les dates (sans email créateur / compléteur). */
  showTaskUserEmails?: boolean
}

function TasksSection({ tasks, onNewTask, onToggleTask, onOpenTask, onEditTask, onDeleteTask, onReorderTasks, onMoveTask, loading = false, updatingTaskId = null, isAdmin = true, showTaskUserEmails = true }: TasksSectionProps) {
  const [activeId, setActiveId] = useState<number | null>(null)
  const [overColumnId, setOverColumnId] = useState<string | null>(null)

  // Sort tasks by ordre
  const sortedTodoTasks = useMemo(() => {
    return [...tasks.todo].sort((a, b) => a.ordre - b.ordre)
  }, [tasks.todo])

  const sortedCompletedTasks = useMemo(() => {
    return [...tasks.completed].sort((a, b) => a.ordre - b.ordre)
  }, [tasks.completed])

  const activeTask = useMemo(() => {
    if (!activeId) return null
    return sortedTodoTasks.find(t => t.id === activeId) || sortedCompletedTasks.find(t => t.id === activeId) || null
  }, [activeId, sortedTodoTasks, sortedCompletedTasks])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as number)
  }

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event
    if (over) {
      const overId = over.id as string | number
      if (overId === 'droppable-todo' || overId === 'droppable-completed') {
        setOverColumnId(overId as string)
      } else {
        // Check if over a task in a different column
        const overTask = sortedTodoTasks.find(t => t.id === overId) || sortedCompletedTasks.find(t => t.id === overId)
        if (overTask && activeId) {
          const activeStatus = sortedTodoTasks.some(t => t.id === activeId) ? 'a_effectuer' : 'realisee'
          const overStatus = sortedTodoTasks.some(t => t.id === overId) ? 'a_effectuer' : 'realisee'
          if (activeStatus !== overStatus) {
            setOverColumnId(overStatus === 'a_effectuer' ? 'droppable-todo' : 'droppable-completed')
          } else {
            setOverColumnId(null)
          }
        } else {
          setOverColumnId(null)
        }
      }
    } else {
      setOverColumnId(null)
    }
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event

    setActiveId(null)
    setOverColumnId(null)

    if (!over) {
      return
    }

    const activeId = active.id as number
    const overId = over.id as string | number

    // Find which column the active task belongs to
    const activeTask = sortedTodoTasks.find(t => t.id === activeId) || sortedCompletedTasks.find(t => t.id === activeId)
    if (!activeTask) return

    const activeStatus: 'a_effectuer' | 'realisee' = sortedTodoTasks.some(t => t.id === activeId) ? 'a_effectuer' : 'realisee'

    // Check if dropped on a droppable column (empty area)
    if (overId === 'droppable-todo' || overId === 'droppable-completed') {
      const newStatus = overId === 'droppable-todo' ? 'a_effectuer' : 'realisee'

      // If status changed, move the task to the end of the new column
      if (activeStatus !== newStatus && onMoveTask) {
        const targetList = newStatus === 'a_effectuer' ? sortedTodoTasks : sortedCompletedTasks
        await onMoveTask(activeId, newStatus, targetList.length)
        return
      }
      return
    }

    // Check if dropped on another task
    const overTask = sortedTodoTasks.find(t => t.id === overId) || sortedCompletedTasks.find(t => t.id === overId)
    if (overTask) {
      const overStatus: 'a_effectuer' | 'realisee' = sortedTodoTasks.some(t => t.id === overId) ? 'a_effectuer' : 'realisee'

      // If dropped in different column, move the task
      if (activeStatus !== overStatus && onMoveTask) {
        const targetList = overStatus === 'a_effectuer' ? sortedTodoTasks : sortedCompletedTasks
        const newIndex = targetList.findIndex(t => t.id === overId)
        await onMoveTask(activeId, overStatus, newIndex >= 0 ? newIndex : targetList.length)
        return
      }

      // Same column, just reorder
      if (activeStatus === 'a_effectuer') {
        const oldIndex = sortedTodoTasks.findIndex((task) => task.id === activeId)
        const newIndex = sortedTodoTasks.findIndex((task) => task.id === overId)

        if (oldIndex !== -1 && newIndex !== -1) {
          const newOrder = arrayMove(sortedTodoTasks, oldIndex, newIndex)
          const taskIds = newOrder.map((task) => task.id)
          await onReorderTasks(taskIds, 'a_effectuer')
        }
      } else {
        const oldIndex = sortedCompletedTasks.findIndex((task) => task.id === activeId)
        const newIndex = sortedCompletedTasks.findIndex((task) => task.id === overId)

        if (oldIndex !== -1 && newIndex !== -1) {
          const newOrder = arrayMove(sortedCompletedTasks, oldIndex, newIndex)
          const taskIds = newOrder.map((task) => task.id)
          await onReorderTasks(taskIds, 'realisee')
        }
      }
    }
  }

  // Droppable column component
  const DroppableColumn = ({ id, children }: { id: string, children: React.ReactNode }) => {
    const { setNodeRef, isOver } = useDroppable({ id })
    const isActive = overColumnId === id && activeId !== null

    return (
      <div
        ref={setNodeRef}
        className={`h-full transition-all duration-200 ${isActive
          ? 'ring-2 ring-brand-bright ring-offset-2 rounded-[18px]'
          : isOver
            ? 'ring-2 ring-brand-bright/60 ring-offset-1 rounded-[18px]'
            : ''
          }`}
      >
        {children}
      </div>
    )
  }

  return (
    <section aria-labelledby="tasks-title" className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-[#2563EB]">
            <CheckCircle2 className="w-[18px] h-[18px] text-white" />
          </span>
          <h2 id="tasks-title" className="text-[22px] font-semibold text-brand-navy">Tâches du projet</h2>
        </div>
        <div className="flex items-center gap-3">
          {isAdmin && (
            <Button
              onClick={onNewTask}
              className="h-[38px] rounded-[10px] bg-brand text-white hover:bg-brand-dark"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle Tâche
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <PageSkeleton variant="rows" />
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <DroppableColumn id="droppable-todo">
              <div className="h-full flex flex-col min-h-0 overflow-hidden rounded-[18px] border border-gray-200 bg-white">
                <div className="flex items-center gap-2.5 bg-[#E8F0FE] px-5 py-3.5 flex-shrink-0">
                  <Circle className="w-4 h-4 text-[#1D4ED8]" />
                  <span className="text-sm font-semibold text-[#1D4ED8]">À effectuer</span>
                  <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-[#1D4ED8]">
                    {tasks.todo.length}
                  </span>
                  <span className="text-xs text-[#1D4ED8]">tâches en cours</span>
                </div>
                <SortableContext
                  items={sortedTodoTasks.map((task) => task.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="min-h-0 h-[min(60vh,560px)] overflow-y-auto overflow-x-hidden custom-scrollbar px-3 py-3">
                    <div className="space-y-2">
                      {sortedTodoTasks.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">Aucune tâche à effectuer</p>
                      ) : (
                        sortedTodoTasks.map((task) => (
                          <SortableTaskCard
                            key={task.id}
                            task={task}
                            isCompleted={false}
                            onToggle={isAdmin ? () => onToggleTask(task.id) : undefined}
                            onOpen={onOpenTask ? () => onOpenTask(task.id) : undefined}
                            onEdit={isAdmin ? () => onEditTask(task.id) : undefined}
                            onDelete={isAdmin ? () => onDeleteTask(task.id, task.title) : undefined}
                            isUpdating={updatingTaskId === task.id}
                            isAdmin={isAdmin}
                            showTaskUserEmails={showTaskUserEmails}
                          />
                        ))
                      )}
                    </div>
                  </div>
                </SortableContext>
              </div>
            </DroppableColumn>

            <DroppableColumn id="droppable-completed">
              <div className="h-full flex flex-col min-h-0 overflow-hidden rounded-[18px] border border-gray-200 bg-white">
                <div className="flex items-center gap-2.5 bg-[#E7F6EE] px-5 py-3.5 flex-shrink-0">
                  <CheckCircle2 className="w-4 h-4 text-[#0B6B3A]" />
                  <span className="text-sm font-semibold text-[#0B6B3A]">Réalisées</span>
                  <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-[#0B6B3A]">
                    {sortedCompletedTasks.length}
                  </span>
                  <span className="text-xs text-[#0B6B3A]">tâches terminées</span>
                </div>
                <SortableContext
                  items={sortedCompletedTasks.map((task) => task.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="min-h-0 h-[min(60vh,560px)] overflow-y-auto overflow-x-hidden custom-scrollbar px-3 py-3">
                    <div className="space-y-2">
                      {sortedCompletedTasks.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">Aucune tâche terminée</p>
                      ) : (
                        sortedCompletedTasks.map((task) => (
                          <SortableTaskCard
                            key={task.id}
                            task={task}
                            isCompleted={true}
                            onToggle={isAdmin ? () => onToggleTask(task.id) : undefined}
                            onOpen={onOpenTask ? () => onOpenTask(task.id) : undefined}
                            onEdit={isAdmin ? () => onEditTask(task.id) : undefined}
                            onDelete={isAdmin ? () => onDeleteTask(task.id, task.title) : undefined}
                            isUpdating={updatingTaskId === task.id}
                            isAdmin={isAdmin}
                            showTaskUserEmails={showTaskUserEmails}
                          />
                        ))
                      )}
                    </div>
                  </div>
                </SortableContext>
              </div>
            </DroppableColumn>
          </div>
          <DragOverlay>
            {activeTask ? (
              <div className="p-4 rounded-lg bg-white border-2 border-cyan-400 shadow-xl opacity-95 rotate-2 transform scale-105">
                <div className="flex items-start gap-3">
                  <GripVertical className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className="font-medium text-gray-800 break-words flex-1 min-w-0">{activeTask.title}</h3>
                      {activeTask.type && (
                        <span className="flex-shrink-0">
                          {activeTask.type === 'Ajustement' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                              <Wrench className="w-3.5 h-3.5" />
                              Ajustement
                            </span>
                          ) : activeTask.type === 'Feature' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-cyan-100 text-cyan-800 border border-cyan-200">
                              <Sparkles className="w-3.5 h-3.5" />
                              Feature
                            </span>
                          ) : null}
                        </span>
                      )}
                    </div>
                    {activeTask.description ? (
                      <TextWithLinks
                        text={activeTask.description}
                        className="text-sm text-gray-600 mb-2"
                      />
                    ) : null}
                    {activeTask.imageUrl && (
                      <div className="mb-2 rounded-lg overflow-hidden border border-gray-200 bg-gray-50 max-h-32">
                        <img src={activeTask.imageUrl} alt="" className="w-full h-auto object-contain max-h-28 object-center" />
                      </div>
                    )}
                    {activeTask.deadlineAt ? (
                      <p className="text-xs text-gray-500 mb-1">Échéance {activeTask.deadlineAt}</p>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
    </section>
  )
}

interface TaskCardProps {
  task: {
    id: number
    title: string
    description: string
    type?: string
    imageUrl?: string | null
    createdAt?: string
    completedAt?: string
    visibleToClient?: boolean
    createdByEmail?: string
    completedByEmail?: string
    deadlineAt?: string
  }
  isCompleted: boolean
  onToggle?: () => void
  onOpen?: () => void
  onEdit?: () => void
  onDelete?: () => void
  isUpdating?: boolean
  isAdmin?: boolean
  showTaskUserEmails?: boolean
}

function SortableTaskCard({ task, isCompleted, onToggle, onOpen, onEdit, onDelete, isUpdating = false, isAdmin = true, showTaskUserEmails = true }: TaskCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition,
    opacity: isDragging ? 0.3 : 1,
  }

  return (
    <div ref={setNodeRef} style={style}>
      <div
        className={`p-3.5 transition-all duration-200 rounded-xl ${isDragging
          ? 'shadow-lg scale-95'
          : 'hover:bg-brand-canvas'
          } ${isUpdating ? 'opacity-50 pointer-events-none' : ''}`}
      >
        <div className="flex items-start gap-3">
          {isAdmin && (
            <div
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing flex-shrink-0 mt-0.5 text-gray-400 hover:text-gray-600 transition-colors"
              title="Glisser pour réordonner"
            >
              <GripVertical className="w-5 h-5" />
            </div>
          )}
          <div
            className={onToggle ? 'cursor-pointer flex-shrink-0' : ''}
            onClick={(e) => {
              e.stopPropagation()
              onToggle?.()
            }}
            role={onToggle ? 'button' : undefined}
            aria-label={onToggle ? (isCompleted ? 'Marquer non réalisée' : 'Marquer réalisée') : undefined}
          >
            {isCompleted ? (
              <div className="w-5 h-5 rounded-full bg-[#16A34A] flex items-center justify-center flex-shrink-0 mt-0.5 hover:bg-[#15803D] transition-colors">
                <Check className="w-3 h-3 text-white" strokeWidth={3.5} />
              </div>
            ) : (
              <Circle className="w-5 h-5 text-[#93B4F5] flex-shrink-0 mt-0.5 hover:text-[#2563EB] transition-colors" />
            )}
          </div>
          <div
            className={`flex-1 min-w-0 ${onOpen ? 'cursor-pointer' : ''}`}
            onClick={(e) => {
              e.stopPropagation()
              onOpen?.()
            }}
            role={onOpen ? 'button' : undefined}
            aria-label={onOpen ? 'Ouvrir la tâche' : undefined}
          >
            <div className="flex items-start justify-between gap-2 mb-1">
              <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
                <h3 className={`text-sm font-medium break-words whitespace-normal ${isCompleted ? 'text-gray-500 line-through' : 'text-brand-navy'}`}>{task.title}</h3>
                {isAdmin && task.visibleToClient && (
                  <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-200 shrink-0">
                    Visible client
                  </Badge>
                )}
                {isAdmin && task.visibleToClient === false && (
                  <Badge variant="secondary" className="bg-gray-100 text-gray-600 border-gray-200 shrink-0">
                    Masqué client
                  </Badge>
                )}
              </div>
              {task.type && (
                <span className="flex-shrink-0">
                  {task.type === 'Ajustement' ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                      <Wrench className="w-3.5 h-3.5" />
                      Ajustement
                    </span>
                  ) : task.type === 'Feature' ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-cyan-100 text-cyan-800 border border-cyan-200">
                      <Sparkles className="w-3.5 h-3.5" />
                      Feature
                    </span>
                  ) : null}
                </span>
              )}
            </div>
            {task.description ? (
              <TextWithLinks
                text={task.description}
                className={`text-sm text-gray-600 mb-2 ${isCompleted ? 'line-through' : ''}`}
              />
            ) : null}
            {task.imageUrl && (
              <div className="mb-2 rounded-lg overflow-hidden border border-gray-200 bg-gray-50 max-h-48">
                <img src={task.imageUrl} alt="" className="w-full h-auto object-contain max-h-44 object-center" />
              </div>
            )}
            {task.deadlineAt ? (
              <p className="mb-1.5 inline-flex rounded-md bg-[#FFF1DC] px-2 py-0.5 text-xs font-medium text-[#93530B]">Échéance {task.deadlineAt}</p>
            ) : null}
            <div className="text-xs text-gray-500 space-y-0.5">
              {isCompleted ? (
                <>
                  <p>
                    Créée le {task.createdAt}
                    {showTaskUserEmails && task.createdByEmail ? ` par ${task.createdByEmail}` : ''}
                  </p>
                  <p>
                    Complétée le {task.completedAt}
                    {showTaskUserEmails && task.completedByEmail ? ` par ${task.completedByEmail}` : ''}
                  </p>
                </>
              ) : (
                <p>
                  Créée le {task.createdAt}
                  {showTaskUserEmails && task.createdByEmail ? ` par ${task.createdByEmail}` : ''}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {onEdit && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onEdit()
                }}
                className="flex-shrink-0 p-2 text-gray-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors"
                title="Modifier la tâche"
              >
                <Edit className="w-4 h-4" />
              </button>
            )}
            {onDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onDelete()
                }}
                className="flex-shrink-0 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                title="Supprimer la tâche"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

interface MetricsSectionProps {
  metrics: Array<{
    id: number
    name: string
    description: string
    currentValue: string
    unit: string
    visibleToClient: boolean
    type: 'incremental' | 'overwrite'
    endpoint: string
    apiKey: string
    data: Array<{ date: string; value: number; created_at?: string }>
  }>
  onNewMetric: () => void
  onEditMetric?: (metricId: number) => void
  onDeleteMetric?: (metricId: number, metricName: string) => void
  sortOrder: 'newest' | 'oldest'
  onSortOrderChange: (order: 'newest' | 'oldest') => void
  loading?: boolean
  isAdmin?: boolean
}

/** Couleurs attribuées aux métriques, dans l'ordre d'affichage. */
const METRIC_ACCENTS = [
  { stroke: '#0B7D8E', fill: '#12A9BD', text: 'text-brand', soft: 'bg-brand-light', softText: 'text-brand-dark' },
  { stroke: '#6D5BD0', fill: '#7C6AE0', text: 'text-brand-violet-dark', soft: 'bg-brand-violet-light', softText: 'text-brand-violet-dark' },
  { stroke: '#2563EB', fill: '#3B82F6', text: 'text-[#1D4ED8]', soft: 'bg-[#E8F0FE]', softText: 'text-[#1D4ED8]' },
  { stroke: '#16A34A', fill: '#22C55E', text: 'text-[#0B6B3A]', soft: 'bg-[#E7F6EE]', softText: 'text-[#0B6B3A]' },
] as const

type MetricAccent = (typeof METRIC_ACCENTS)[number]

const PERIOD_OPTIONS = ['7', '30', '90', 'all'] as const

function MetricsSection({ metrics, onNewMetric, onEditMetric, onDeleteMetric, sortOrder, onSortOrderChange, loading = false, isAdmin = true }: MetricsSectionProps) {
  const [selectedPeriod, setSelectedPeriod] = useState<(typeof PERIOD_OPTIONS)[number]>('all')

  // Filter metrics data based on selected period
  const getFilteredMetrics = () => {
    if (selectedPeriod === 'all') {
      return metrics
    }

    const days = parseInt(selectedPeriod)
    const cutoffDate = new Date()
    cutoffDate.setDate(cutoffDate.getDate() - days)
    cutoffDate.setHours(0, 0, 0, 0)

    return metrics.map(metric => {
      // Filter data points based on the cutoff date
      const filteredData = metric.data.filter(point => {
        // Use created_at if available (preferred method)
        if (point.created_at) {
          const pointDate = new Date(point.created_at)
          pointDate.setHours(0, 0, 0, 0)
          return pointDate >= cutoffDate
        }
        // Fallback: parse the date string (format: "DD/MM")
        const dateParts = point.date.split('/')
        if (dateParts.length === 2) {
          const day = parseInt(dateParts[0])
          const month = parseInt(dateParts[1]) - 1
          const currentYear = new Date().getFullYear()
          const pointDate = new Date(currentYear, month, day)
          pointDate.setHours(0, 0, 0, 0)
          return pointDate >= cutoffDate
        }
        return true
      })

      return {
        ...metric,
        data: filteredData,
        currentValue: filteredData.length > 0 ? filteredData[filteredData.length - 1].value.toString() : '0'
      }
    })
  }

  const filteredMetricsUnsorted = getFilteredMetrics()
  // Sort filtered metrics based on sortOrder
  const filteredMetrics = [...filteredMetricsUnsorted].sort((a, b) => {
    return sortOrder === 'newest' ? b.id - a.id : a.id - b.id
  })
  // Une seule métrique : carte pleine largeur, chiffre à gauche et courbe à droite
  const isSingle = filteredMetrics.length === 1

  return (
    <section aria-labelledby="metrics-title" className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-brand">
            <TrendingUp className="w-[18px] h-[18px] text-white" />
          </span>
          <h2 id="metrics-title" className="text-[22px] font-semibold text-brand-navy">
            Métriques {isAdmin ? <span className="text-base font-normal text-gray-500">(vue complète)</span> : null}
          </h2>
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          <div role="group" aria-label="Période" className="flex rounded-xl border border-gray-200 bg-white p-1">
            {PERIOD_OPTIONS.map((period) => (
              <button
                key={period}
                type="button"
                onClick={() => setSelectedPeriod(period)}
                aria-pressed={selectedPeriod === period}
                className={`rounded-lg px-3 py-1.5 text-[13px] transition-colors ${selectedPeriod === period ? 'bg-brand-navy font-medium text-white' : 'text-gray-600 hover:text-brand-navy'}`}
              >
                {period === 'all' ? 'Tout' : `${period} jours`}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-1.5 text-[13px] text-gray-600">
            Tri
            <select
              value={sortOrder}
              onChange={(e) => onSortOrderChange(e.target.value as 'newest' | 'oldest')}
              className="h-[38px] rounded-[10px] border border-gray-300 bg-white px-2.5 text-[13px] text-brand-navy focus:border-brand focus:ring-brand"
            >
              <option value="newest">Plus récentes</option>
              <option value="oldest">Plus anciennes</option>
            </select>
          </label>
          {isAdmin && (
            <Button
              onClick={onNewMetric}
              className="h-[38px] rounded-[10px] bg-brand text-white hover:bg-brand-dark"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle Métrique
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <PageSkeleton variant="rows" />
      ) : metrics.length === 0 ? (
        <div className="rounded-[18px] border border-gray-200 bg-white py-10 text-center text-gray-500">Aucune métrique disponible</div>
      ) : (
        <div className={isSingle ? '' : 'grid grid-cols-1 gap-5 xl:grid-cols-2'}>
          {filteredMetrics.map((metric, index) => (
            <MetricCard
              key={metric.id}
              metric={metric}
              accent={METRIC_ACCENTS[index % METRIC_ACCENTS.length]}
              wide={isSingle}
              isAdmin={isAdmin}
              onEdit={onEditMetric ? () => onEditMetric(metric.id) : undefined}
              onDelete={onDeleteMetric ? () => onDeleteMetric(metric.id, metric.name) : undefined}
            />
          ))}
        </div>
      )}
    </section>
  )
}

function MetricCard({ metric, accent, wide = false, isAdmin = true, onEdit, onDelete }: {
  metric: MetricsSectionProps['metrics'][0]
  accent: MetricAccent
  wide?: boolean
  isAdmin?: boolean
  onEdit?: () => void
  onDelete?: () => void
}) {
  // Aggregate data by day for incremental mode
  const aggregatedData = useMemo(() => {
    if (metric.data.length === 0) {
      return []
    }

    // Ensure chronological order and robust day grouping (avoid collapsing different years/months)
    const parsedPoints = metric.data.map((point) => {
      const baseDate = point.created_at
        ? new Date(point.created_at)
        : (() => {
          const [day, month] = point.date.split('/').map(Number)
          const year = new Date().getFullYear()
          return new Date(year, (month || 1) - 1, day || 1)
        })()

      return {
        sortKey: baseDate.getTime(),
        label: baseDate.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
        value: Number(point.value) || 0,
      }
    })

    // For overwrite mode, just sort chronologically without grouping
    if (metric.type !== 'incremental') {
      return parsedPoints
        .sort((a, b) => a.sortKey - b.sortKey)
        .map(({ label, value }) => ({ date: label, value }))
    }

    // Incremental: group by day and sum values
    const groupedByDay: Record<string, { total: number; label: string; sortKey: number }> = {}
    parsedPoints.forEach(({ sortKey, label, value }) => {
      // Use YYYY-MM-DD as key to avoid merging different years
      const isoKey = new Date(sortKey).toISOString().slice(0, 10)
      if (!groupedByDay[isoKey]) {
        groupedByDay[isoKey] = { total: 0, label, sortKey }
      }
      groupedByDay[isoKey].total += value
    })

    return Object.values(groupedByDay)
      .sort((a, b) => a.sortKey - b.sortKey)
      .map(({ label, total }) => ({ date: label, value: total }))
  }, [metric.data, metric.type])

  // Format value for display
  const formatValue = (value: number): string => {
    if (value >= 1000000) {
      return (value / 1000000).toFixed(1) + 'M'
    } else if (value >= 1000) {
      return (value / 1000).toFixed(1) + 'k'
    }
    return value.toLocaleString('fr-FR')
  }

  // Value shown on the card header
  const displayValue = useMemo(() => {
    if (aggregatedData.length === 0) return 0
    if (metric.type === 'incremental') {
      return aggregatedData.reduce((sum, point) => sum + (point.value || 0), 0)
    }
    return aggregatedData[aggregatedData.length - 1].value
  }, [aggregatedData, metric.type])

  const gradientId = `metric-fill-${metric.id}`

  const chart = aggregatedData.length === 0 ? (
    <div className="flex h-[220px] items-center justify-center rounded-xl bg-brand-canvas text-sm text-gray-500">
      Aucune donnée disponible
    </div>
  ) : (
    <div className="h-[220px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={aggregatedData} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={accent.fill} stopOpacity={0.28} />
              <stop offset="100%" stopColor={accent.fill} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} stroke="#EEF0F3" />
          <XAxis
            dataKey="date"
            tickLine={false}
            axisLine={false}
            tickMargin={10}
            minTickGap={28}
            tick={{ fontSize: 12, fill: '#667085' }}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            width={44}
            tick={{ fontSize: 12, fill: '#667085' }}
            tickFormatter={(v) => formatValue(Number(v))}
          />
          <Tooltip
            cursor={{ stroke: accent.stroke, strokeOpacity: 0.3, strokeDasharray: '4 4' }}
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null
              return (
                <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm shadow-lg">
                  <div className="text-xs text-gray-500">{label}</div>
                  <div className="font-medium text-brand-navy">
                    Total : {formatValue(Number(payload[0].value))}{metric.unit ? ` ${metric.unit}` : ''}
                  </div>
                </div>
              )
            }}
          />
          <Area
            dataKey="value"
            type="monotone"
            stroke={accent.stroke}
            strokeWidth={2.5}
            fill={`url(#${gradientId})`}
            dot={false}
            activeDot={{ r: 5, fill: '#FFFFFF', stroke: accent.stroke, strokeWidth: 2.5 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )

  const adminBadges = isAdmin ? (
    <div className="flex flex-wrap items-center gap-1.5">
      {metric.visibleToClient ? (
        <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-200">
          Visible client
        </Badge>
      ) : (
        <Badge variant="secondary" className="bg-gray-100 text-gray-600 border-gray-200">
          Masqué client
        </Badge>
      )}
      <Badge variant="secondary" className="bg-brand-light text-brand-dark border-brand-light">
        {metric.type === 'incremental' ? 'Incrémental' : 'Écrasement'}
      </Badge>
    </div>
  ) : null

  const adminActions = isAdmin && (onEdit || onDelete) ? (
    <div className="flex items-center gap-1">
      {onEdit && (
        <button
          onClick={onEdit}
          className="p-2 text-gray-400 hover:text-brand hover:bg-brand-light rounded-lg transition-colors"
          title="Modifier la métrique"
          aria-label="Modifier la métrique"
        >
          <Edit className="w-4 h-4" />
        </button>
      )}
      {onDelete && (
        <button
          onClick={onDelete}
          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
          title="Supprimer la métrique"
          aria-label="Supprimer la métrique"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  ) : null

  const valueBlock = (
    <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
      <span className={`text-5xl font-semibold tabular-nums leading-none tracking-tight ${accent.text}`}>
        {formatValue(displayValue)}
      </span>
      {metric.unit && <span className="text-sm text-gray-600">{metric.unit}</span>}
    </div>
  )

  const typeLabel = (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${accent.soft} ${accent.softText}`}>
      {metric.type === 'incremental' ? 'Total cumulé' : 'Valeur actuelle'}
    </span>
  )

  return (
    <article className="rounded-[18px] border border-gray-200 bg-white">
      {wide ? (
        <div className="flex flex-wrap items-stretch gap-7 p-6">
          <div className={`flex min-w-[220px] flex-[1_1_220px] flex-col gap-2.5 rounded-[14px] p-5 ${accent.soft}`}>
            <div className="flex items-start justify-between gap-2">
              <h3 className="text-base font-semibold text-brand-navy">{metric.name}</h3>
              {adminActions}
            </div>
            {metric.description && <p className={`text-[13px] ${accent.softText}`}>{metric.description}</p>}
            {adminBadges}
            <div className="mt-auto pt-3">{valueBlock}</div>
            <div>{typeLabel}</div>
          </div>
          <div className="min-w-0 flex-[3_1_420px] self-center">{chart}</div>
        </div>
      ) : (
        <div className="flex flex-col gap-5 p-6">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 space-y-1.5">
              <h3 className="text-base font-semibold text-brand-navy">{metric.name}</h3>
              {metric.description && <p className="text-[13px] text-gray-500">{metric.description}</p>}
              {adminBadges}
            </div>
            <div className="flex items-start gap-2">
              <span className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${accent.soft}`}>
                <TrendingUp className={`w-[18px] h-[18px] ${accent.softText}`} />
              </span>
              {adminActions}
            </div>
          </div>
          <div className="flex flex-wrap items-end justify-between gap-3">
            {valueBlock}
            {typeLabel}
          </div>
          {chart}
        </div>
      )}

      {isAdmin && (
        <div className="border-t border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Code className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium text-gray-700">
                Configuration API (Admin uniquement)
              </span>
            </div>
            <a
              href="#api-documentation"
              onClick={(e) => {
                e.preventDefault()
                // Show documentation modal or scroll to doc section
                alert(`📖 Documentation API Métriques\n\n` +
                  `Pour envoyer des données à cette métrique, effectuez une requête POST :\n\n` +
                  `curl -X POST "${metric.endpoint}" \\\n` +
                  `  -H "Content-Type: application/json" \\\n` +
                  `  -H "X-API-Token: ${metric.apiKey}" \\\n` +
                  `  -d '{"value": "123"}'\n\n` +
                  `📌 Headers requis:\n` +
                  `  • Content-Type: application/json\n` +
                  `  • X-API-Token: votre clé API\n\n` +
                  `📌 Body (JSON):\n` +
                  `  • value: la valeur à enregistrer (string ou number)`)
              }}
              className="text-sm text-brand hover:text-brand-dark hover:underline flex items-center gap-1"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Documentation API
            </a>
          </div>
          <div className="space-y-3">
            <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
              <label className="text-sm font-medium text-gray-700 flex-shrink-0">Endpoint :</label>
              <input
                type="text"
                value={metric.endpoint}
                readOnly
                className="w-full min-w-0 sm:max-w-[460px] px-3 py-2 text-sm bg-white border border-gray-200 rounded-md text-left"
              />
            </div>
            <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
              <label className="text-sm font-medium text-gray-700 flex-shrink-0">API Key :</label>
              <input
                type="text"
                value={metric.apiKey}
                readOnly
                className="w-full min-w-0 sm:max-w-[460px] px-3 py-2 text-sm bg-white border border-gray-200 rounded-md text-left"
              />
            </div>
          </div>

          {/* Documentation rapide */}
          <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200 mb-2">
            <p className="text-xs text-gray-600 font-medium mb-2 leading-relaxed">
              💡 Exemple de requête :
            </p>
            <pre className="text-xs text-gray-700 bg-gray-100 p-3 rounded overflow-x-auto whitespace-pre-wrap leading-relaxed">
              {`curl -X POST "${metric.endpoint}" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Token: ${metric.apiKey}" \\
  -d '{"value": "123"}'`}
            </pre>
          </div>
        </div>
      )}
    </article>
  )
}
